import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownIcon } from "@heroicons/react/outline";
import {
  getTERCategory,
  calculateTERRate,
  POSITION_COST_PERCENTAGE,
  POSITION_COST_MAX,
  PENSION_COST_PERCENTAGE,
  PENSION_COST_MAX,
  getPTKPRate,
  TAX_BRACKETS,
  calculateGrossFromNet,
  calculateLastTaxPeriod,
  calculatePPh21NonPermanentDaily,
  calculateProgressiveTax,
  roundTaxableIncome,
} from "../../../Constants/terRates";
import {
  CONTRIBUTION_CONFIGS,
  getContributionMaxAmount,
} from "../../../Constants/bpjs";
import {
  calculateMonthsDifference,
  formatMoney,
  parseCurrencyValue,
} from "../../../Utils/StringUtils";
import FormInput from "../../../Components/v2/Form/FormInput";
import FormLabel from "../../../Components/v2/Form/FormLabel";
import FormSelect from "../../../Components/v2/Form/FormSelect";
import FormRadioGroup from "../../../Components/v2/Form/FormRadioGroup";
import FormDate from "../../../Components/v2/Form/FormDate";
import { Modal } from "../../../Components/Modal";
import {
  TAX_METHOD_OPTIONS,
  MARRIAGE_STATUS_OPTIONS,
  DEPENDENT_OPTIONS,
} from "../../../Constants/business";
import { Button } from "../../../Components/Button";
import { MONTH_NAMES } from "../../../Utils";
import {
  CALCULATION_PERIOD_OPTIONS,
  CALCULATION_METHOD_OPTIONS,
  INPUT_MODE_OPTIONS,
  EMPLOYEE_TYPE_OPTIONS,
  FORMER_EMPLOYEE_TYPES,
  PERIOD_TYPE_OPTIONS,
  PROGRESSIVE_TAX_TYPES,
  WAGE_TYPE_OPTIONS,
} from "../../../Constants/calculator";
import TaxCalculationExamples from "../../../Components/TaxCalculationExamples";
import { PPH_2126_EXAMPLES } from "../../../Constants/pph2126Examples";
import { PPH2126_KB_KEYS } from "../../../Constants/pph2126KnowledgeBase";
import KnowledgeBaseSection from "../../../Components/Calculator/KnowledgeBaseSection";
import {
  CalculatorCard,
  ResultRow,
  LedgerSection,
  ResultHeadline,
  SectionHeading,
  DetailBox,
  DetailRow,
  FormulaRow,
  InfoBadge,
  InlineEditButton,
  ResetButton,
} from "../../../Components/Calculator/components";

// The quiet "change to..." links that sit at the end of a field label. They are
// deliberately shorter than a touch target: the label row sets how far every
// input in the grid sits below the top of its cell, so a control that is taller
// than the label pushes its own field out of line with the one beside it. The
// hit area is put back with a pseudo-element, which does not affect layout. It
// stops at 4px because the label row's margin collapses with the input's, so a
// taller one would reach over the top edge of the field it labels and swallow
// clicks meant for it.
const QUIET_LINK_CLASS =
  "relative rounded-md px-1 text-xs font-medium leading-5 text-slate-500 underline decoration-slate-300 underline-offset-2 transition-colors after:absolute after:-inset-x-1 after:-inset-y-1 after:content-[''] hover:text-slate-900 hover:decoration-slate-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

// Fragment the result links to. It shows in the address bar, so it is written
// in the language of the page.
const MONTHLY_BREAKDOWN_ID = "rincian-pajak-per-bulan";

// Short fact labels in the result caption ("Tahunan 2026", "Gross Up") never
// break inside themselves: "Gross" at the end of one line and "Up" on the next
// reads as two facts. No-break spaces keep each one whole.
const keepTogether = (text) => text.replace(/ /g, "\u00A0");

const CalculatorPph2126 = () => {
  const { t } = useTranslation();
  const resultRef = useRef(null);

  // Form state
  const [employeeType, setEmployeeType] = useState(EMPLOYEE_TYPE_OPTIONS[0]);
  const [taxScheme, setTaxScheme] = useState(TAX_METHOD_OPTIONS[0]);
  const [calculationPeriod, setCalculationPeriod] = useState(
    CALCULATION_PERIOD_OPTIONS[0],
  );
  const [calculationMethod, setCalculationMethod] = useState(
    CALCULATION_METHOD_OPTIONS[0],
  );
  const [inputMode, setInputMode] = useState(INPUT_MODE_OPTIONS[0]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [grossSalary, setGrossSalary] = useState("");
  const [allowances, setAllowances] = useState("");
  const [variableAllowances, setVariableAllowances] = useState("");
  const [bonus, setBonus] = useState("");
  const [thr, setThr] = useState("");
  const [overtime, setOvertime] = useState("");
  const [premium, setPremium] = useState("");
  const [otherIncome, setOtherIncome] = useState("");
  const [marriageStatus, setMarriageStatus] = useState(
    MARRIAGE_STATUS_OPTIONS[0],
  );
  const [dependents, setDependents] = useState(null);
  const [isMonthly, setIsMonthly] = useState(true);
  const [wageType, setWageType] = useState(WAGE_TYPE_OPTIONS[0]);
  const [contractTotal, setContractTotal] = useState("");
  const [workingDays, setWorkingDays] = useState("");
  const [hasBPJS, setHasBPJS] = useState(true);
  const [hasPensionContribution, setHasPensionContribution] = useState(false);
  const [hasZakat, setHasZakat] = useState(false);

  const [customBpjsJHT, setCustomBpjsJHT] = useState(null);
  const [customBpjsJP, setCustomBpjsJP] = useState(null);
  const [customPensionAmount, setCustomPensionAmount] = useState(null);
  const [customZakatAmount, setCustomZakatAmount] = useState(null);
  const [editingBPJSType, setEditingBPJSType] = useState(null);
  const [tempAmount, setTempAmount] = useState("");
  const [modalInputMode, setModalInputMode] = useState("monthly");
  const [bpjsEntryMode, setBpjsEntryMode] = useState("automatic");
  const [selectedYear, setSelectedYear] = useState(null);
  const [pph26Rate, setPph26Rate] = useState(20);
  const [oneTimeFields, setOneTimeFields] = useState({
    bonus: true,
    thr: true,
    overtime: false,
  });

  // Helper constants for employee type checks
  const isPermanentOrRetired =
    employeeType.value === "permanent" || employeeType.value === "retired";
  const hasOtherIncomeField =
    employeeType.value !== "retired" &&
    employeeType.value !== "freelance" &&
    !PROGRESSIVE_TAX_TYPES.includes(employeeType.value);
  const hasMarriageStatusField =
    employeeType.value === "permanent" ||
    employeeType.value === "retired" ||
    employeeType.value === "non-permanent";
  const isYearlyPeriod = calculationPeriod.value === "yearly";
  // The base wage takes the whole row so its "change to..." control has room.
  // A non-permanent employee paid monthly is the one case with a single income
  // field after it, which would otherwise sit alone in half a row.
  const grossSalaryPairsWithNextField =
    employeeType.value === "non-permanent" && isMonthly;
  // Everything in the employee section other than the type itself.
  const hasEmployeeDetailFields =
    hasMarriageStatusField || employeeType.value === "permanent";
  const hasIncomeInput = true;

  const periodTypeOption = useMemo(
    () => PERIOD_TYPE_OPTIONS.find((opt) => opt.value === isMonthly),
    [isMonthly],
  );

  const wageTypeOption = useMemo(
    () => WAGE_TYPE_OPTIONS.find((opt) => opt.value === wageType.value),
    [wageType.value],
  );

  // `periodDate` is the month being priced. The JP cap moves on a fixed date,
  // so a 2024 period is owed the 2024 cap - the Employee path already asks for
  // its own month through getDeductionAmount. Left out, it means today.
  const calculateBPJS = useCallback(
    (grossAmount, periodDate) => {
      if (!hasBPJS || employeeType.value !== "permanent") {
        return { bpjsJHT: 0, bpjsJP: 0 };
      }

      const bpjsJHT =
        grossAmount * CONTRIBUTION_CONFIGS.BPJS_KETENAGAKERJAAN_JHT.percentage;
      const jpAmount =
        grossAmount * CONTRIBUTION_CONFIGS.BPJS_KETENAGAKERJAAN_JP.percentage;
      const bpjsJP = Math.min(
        jpAmount,
        getContributionMaxAmount(
          CONTRIBUTION_CONFIGS.BPJS_KETENAGAKERJAAN_JP,
          periodDate,
        ),
      );

      return { bpjsJHT, bpjsJP };
    },
    [hasBPJS, employeeType.value],
  );

  // The month a single BPJS figure is priced in: the first month of a yearly
  // period (the same month monthlyBreakdown starts from), or today for the
  // monthly period, which has no date to ask.
  const bpjsPricingDate = useMemo(() => {
    if (calculationPeriod.value !== "yearly") return undefined;
    return startDate
      ? new Date(startDate)
      : new Date(new Date().getFullYear(), 0, 1);
  }, [calculationPeriod.value, startDate]);

  const workMonths = useMemo(() => {
    if (!startDate && !endDate) return 12;
    if (!startDate || !endDate) return 1;
    return Math.max(calculateMonthsDifference(startDate, endDate), 1);
  }, [startDate, endDate]);

  // The years the period actually covers - not the years up to today. Ending
  // the list at the current year left a 2024 period offering 2025 and 2026,
  // and a period set in the future with no years to offer at all.
  const yearList = useMemo(() => {
    const currentYear = new Date().getFullYear();

    if (!startDate) {
      return [{ label: currentYear.toString(), value: currentYear }];
    }

    const startYear = new Date(startDate).getFullYear();
    const endYear = endDate ? new Date(endDate).getFullYear() : startYear;
    const years = [];

    for (let year = startYear; year <= Math.max(startYear, endYear); year++) {
      years.push({
        label: year.toString(),
        value: year,
      });
    }

    return years;
  }, [startDate, endDate]);

  // Default to the first year of the period, and drop a selection the new
  // dates no longer cover - every supporting figure is derived from this.
  useEffect(() => {
    if (yearList.length === 0) return;
    setSelectedYear((current) =>
      current !== null && yearList.some((year) => year.value === current)
        ? current
        : yearList[0].value,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearList]);

  const parsedInputs = useMemo(() => {
    let dailyWageAmount = parseCurrencyValue(grossSalary);

    if (!isMonthly && wageType.value === "contract") {
      // PMK 168/2023 knows no rounding here. Rounding to the nearest thousand
      // could move the average daily wage across a TER bracket - Rp450.400 down
      // to Rp450.000 turns 0,5% into 0%.
      // With no days worked there is no daily wage to average out to.
      const contractAmount = parseCurrencyValue(contractTotal);
      const days = parseInt(workingDays) || 0;
      dailyWageAmount = days > 0 ? contractAmount / days : 0;
    }

    return {
      gross: dailyWageAmount,
      allow: parseCurrencyValue(allowances),
      allowVariable: parseCurrencyValue(variableAllowances),
      bonusAmount: parseCurrencyValue(bonus),
      thrAmount: parseCurrencyValue(thr),
      overtimeAmount: parseCurrencyValue(overtime),
      premiumAmount: parseCurrencyValue(premium),
      otherIncomeAmount: parseCurrencyValue(otherIncome),
    };
  }, [
    grossSalary,
    allowances,
    variableAllowances,
    bonus,
    thr,
    overtime,
    premium,
    otherIncome,
    isMonthly,
    wageType.value,
    contractTotal,
    workingDays,
  ]);

  const monthlyBreakdown = useMemo(() => {
    const isYearlyCalculation =
      calculationPeriod.value === "yearly" && isPermanentOrRetired;
    if (!isYearlyCalculation) return null;

    const {
      gross,
      allow,
      allowVariable,
      bonusAmount,
      thrAmount,
      overtimeAmount,
      premiumAmount,
      otherIncomeAmount,
    } = parsedInputs;

    if (gross <= 0) return null;

    const terCategory = getTERCategory(marriageStatus.value, dependents?.value);
    // Under gross-up the user types a net salary, and the tax allowance that
    // covers its PPh 21 is itself income (UU PPh Art. 4(1)), so it adds to
    // gross. Without this, Gross and Gross-Up produce identical figures.
    const isGrossUp = taxScheme.value === "GROSS_UP";

    // "Disetahunkan" is example I.2.1.2 of PMK 168/2023 Appendix B: the
    // subjective tax liability itself begins or ends mid-year. "Kurang dari
    // Setahun" (I.2.1.1) is not annualized.
    const annualizeTaxSubject = calculationMethod.value === "annualized";

    const currentYear = new Date().getFullYear();
    const start = startDate ? new Date(startDate) : new Date(currentYear, 0, 1);
    const end = endDate ? new Date(endDate) : new Date(currentYear, 11, 31);
    const totalWorkMonths = Math.max(
      calculateMonthsDifference(start, end) || 12,
      1,
    );
    const isAnnualInput = inputMode.value === "annual";

    const months = [];
    let cumulativeGross = 0,
      cumulativeTax = 0,
      cumulativePositionCost = 0,
      cumulativeBPJS = 0,
      cumulativeTaxAllowance = 0;

    const currentDate = new Date(start);
    currentDate.setDate(1);
    let monthIndex = 0;
    let currentYearForTracking = currentDate.getFullYear();

    while (currentDate <= end) {
      const isLastMonth = monthIndex === totalWorkMonths - 1;
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();

      if (currentYear !== currentYearForTracking && currentMonth === 0) {
        cumulativeGross = 0;
        cumulativeTax = 0;
        cumulativePositionCost = 0;
        cumulativeBPJS = 0;
        cumulativeTaxAllowance = 0;
        currentYearForTracking = currentYear;
      }

      const monthlyGross = isAnnualInput ? gross / totalWorkMonths : gross;
      let monthlyAllowances = 0;

      if (isAnnualInput) {
        monthlyAllowances =
          (allow +
            allowVariable +
            bonusAmount +
            thrAmount +
            overtimeAmount +
            premiumAmount +
            otherIncomeAmount) /
          totalWorkMonths;
      } else {
        // Monthly input: separate recurring vs one-time fields
        const toggleableFields = {
          bonus: bonusAmount,
          thr: thrAmount,
          overtime: overtimeAmount,
        };
        // Allowances, premium, otherIncome are always monthly
        monthlyAllowances =
          allow + allowVariable + premiumAmount + otherIncomeAmount;
        Object.entries(toggleableFields).forEach(([key, amount]) => {
          if (oneTimeFields[key]) {
            // One-time fields only added in the last month
            if (isLastMonth) {
              monthlyAllowances += amount;
            }
          } else {
            monthlyAllowances += amount;
          }
        });
      }

      const isDecember = currentMonth === 11;

      const nextMonth = new Date(currentDate);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const isLastMonthInCurrentYear =
        isDecember || (nextMonth > end && !isDecember);

      const shouldUseProgressive = isLastMonthInCurrentYear;

      // Under Gross this is already gross. Under Gross-Up it is still net: the
      // TER months are grossed up here, while the last tax period is only
      // known once the yearly tax is calculated below.
      const netMonthlyTotal = monthlyGross + monthlyAllowances;
      let totalMonthlyGross = netMonthlyTotal;
      let monthlyTaxAllowance = 0;

      if (isGrossUp && !shouldUseProgressive) {
        totalMonthlyGross = calculateGrossFromNet(
          monthlyGross,
          monthlyAllowances,
          terCategory,
        );
        monthlyTaxAllowance = totalMonthlyGross - netMonthlyTotal;
      }

      // BPJS employee contributions run on upah, which is gaji pokok plus
      // tunjangan tetap. Tunjangan tidak tetap, bonus, THR, lembur, the JKK/JKM
      // premium and other variable pay are all part of penghasilan bruto but
      // sit outside this base, so they must not be summed in here.
      const monthlyUpah =
        monthlyGross + (isAnnualInput ? allow / totalWorkMonths : allow);
      // Priced at the cap in force in this month. An amount the user entered
      // by hand replaces the automatic one here too, or the edit would move
      // neither the monthly rows nor the tax they add up to.
      const monthlyDefaults = calculateBPJS(monthlyUpah, new Date(currentDate));
      const hasBPJSRows = hasBPJS && employeeType.value === "permanent";
      const monthlyBpjsJHT =
        hasBPJSRows && customBpjsJHT !== null
          ? customBpjsJHT
          : monthlyDefaults.bpjsJHT;
      const monthlyBpjsJP =
        hasBPJSRows && customBpjsJP !== null
          ? customBpjsJP
          : monthlyDefaults.bpjsJP;
      const monthlyPension =
        hasPensionContribution && customPensionAmount ? customPensionAmount : 0;
      const monthlyZakat =
        hasZakat && customZakatAmount ? customZakatAmount : 0;
      const monthlyBPJS =
        monthlyBpjsJHT + monthlyBpjsJP + monthlyPension + monthlyZakat;
      cumulativeBPJS += monthlyBPJS;

      const costPercentage =
        employeeType.value === "retired"
          ? PENSION_COST_PERCENTAGE
          : POSITION_COST_PERCENTAGE;
      const costMaxYearly =
        employeeType.value === "retired" ? PENSION_COST_MAX : POSITION_COST_MAX;
      let pph21 = 0,
        terRate = 0,
        annualisedTaxableIncome = 0,
        method = "TER";

      if (!shouldUseProgressive) {
        terRate = calculateTERRate(totalMonthlyGross, terCategory);
        pph21 = totalMonthlyGross * terRate;
        cumulativeTax += pph21;
      } else {
        method = "Progressive";

        let monthsInCurrentYear = 0;
        const yearStart = new Date(currentYear, 0, 1);
        const tempDate = new Date(
          Math.max(start.getTime(), yearStart.getTime()),
        );
        tempDate.setDate(1);
        while (tempDate <= currentDate) {
          monthsInCurrentYear++;
          tempDate.setMonth(tempDate.getMonth() + 1);
        }

        const maxCostForPeriod = (costMaxYearly / 12) * monthsInCurrentYear;

        // Gross through last month plus this month. Under gross-up this month
        // is still on a net basis, since its tax allowance is the unknown.
        const grossForPeriod = cumulativeGross + totalMonthlyGross;

        // Left uncapped here: the cap is handed to `calculateLastTaxPeriod` as
        // `positionCostMax`, so it still applies after the gross-up allowance
        // has been added to the period's bruto.
        const annualizedPositionCost = grossForPeriod * costPercentage;

        // The gross-up formula derives the yearly tax allowance from net PKP,
        // so the allowance already granted through the TER months has to come
        // back out first. Otherwise it is counted twice and the allowance no
        // longer equals the PPh 21 due on PKP + T.
        const { yearlyTax: totalYearTax, yearlyTaxableIncome } =
          calculateLastTaxPeriod({
            yearlyGrossSalary: grossForPeriod,
            yearlyPositionCost: annualizedPositionCost,
            yearlyDeductions: cumulativeBPJS,
            yearlyTaxPaid: isGrossUp ? cumulativeTaxAllowance : 0,
            marriageStatus: marriageStatus.value,
            dependent: dependents?.value,
            isGrossUp,
            annualise: annualizeTaxSubject,
            monthsWorked: monthsInCurrentYear,
            positionCostMax: maxCostForPeriod,
            // Under gross-up the allowance solved for here is part of this
            // month's bruto and carries its own position cost. This page caps
            // the cost over the whole period rather than month by month, so
            // the monthly cap is left off.
            lastPeriodGross: totalMonthlyGross,
            positionCostMonthlyMax: Infinity,
          });
        annualisedTaxableIncome = annualizeTaxSubject ? yearlyTaxableIncome : 0;

        pph21 = totalYearTax - cumulativeTax;
        cumulativeTax = totalYearTax;

        if (isGrossUp) {
          monthlyTaxAllowance = pph21;
          totalMonthlyGross = netMonthlyTotal + pph21;
        }
      }

      cumulativeGross += totalMonthlyGross;
      cumulativeTaxAllowance += monthlyTaxAllowance;

      const monthlyPositionCost = Math.min(
        totalMonthlyGross * costPercentage,
        costMaxYearly / 12,
      );
      cumulativePositionCost += monthlyPositionCost;

      months.push({
        month: currentMonth,
        year: currentYear,
        monthName: MONTH_NAMES[currentMonth],
        grossIncome: totalMonthlyGross,
        taxAllowance: monthlyTaxAllowance,
        positionCost: monthlyPositionCost,
        bpjs: monthlyBPJS,
        // The deduction breakdown has to add up to the same figure netIncome
        // subtracts, so it reads its parts from here rather than recomputing
        // them from an annual gross that has already been annualised once.
        bpjsJHT: monthlyBpjsJHT,
        bpjsJP: monthlyBpjsJP,
        pension: monthlyPension,
        zakat: monthlyZakat,
        terRate: terRate * 100,
        pph21,
        annualisedTaxableIncome,
        method,
        cumulativeGross,
        cumulativeTax,
      });

      currentDate.setMonth(currentDate.getMonth() + 1);
      monthIndex++;
    }

    return {
      months,
      totalGross: cumulativeGross,
      totalTax: cumulativeTax,
      totalTaxAllowance: cumulativeTaxAllowance,
      totalPositionCost: cumulativePositionCost,
      totalBPJS: cumulativeBPJS,
      terCategory,
    };
  }, [
    parsedInputs,
    taxScheme.value,
    employeeType.value,
    marriageStatus.value,
    dependents?.value,
    hasPensionContribution,
    customPensionAmount,
    hasZakat,
    customZakatAmount,
    calculationPeriod.value,
    inputMode.value,
    startDate,
    endDate,
    calculateBPJS,
    customBpjsJHT,
    customBpjsJP,
    hasBPJS,
    isPermanentOrRetired,
    oneTimeFields,
    calculationMethod.value,
  ]);

  // Calculate results based on selected year
  const yearlyCalculations = useMemo(() => {
    if (!monthlyBreakdown) return null;

    // Determine valid year: use selectedYear if valid, otherwise the first
    // year the period covers
    const validYear =
      selectedYear && yearList.some((y) => y.value === selectedYear)
        ? selectedYear
        : yearList.length > 0
          ? yearList[0].value
          : null;

    if (!validYear) return null;

    const yearMonths = monthlyBreakdown.months.filter(
      (row) => row.year === validYear,
    );

    if (yearMonths.length === 0) return null;

    const totalGross = yearMonths.reduce(
      (sum, row) => sum + row.grossIncome,
      0,
    );
    const totalTax = yearMonths.reduce((sum, row) => sum + row.pph21, 0);
    const totalTaxAllowance = yearMonths.reduce(
      (sum, row) => sum + (row.taxAllowance || 0),
      0,
    );
    const totalPositionCost = yearMonths.reduce(
      (sum, row) => sum + row.positionCost,
      0,
    );
    const totalBPJS = yearMonths.reduce((sum, row) => sum + row.bpjs, 0);
    const sumOf = (key) =>
      yearMonths.reduce((sum, row) => sum + (row[key] || 0), 0);
    const totalBpjsJHT = sumOf("bpjsJHT");
    const totalBpjsJP = sumOf("bpjsJP");
    const totalPension = sumOf("pension");
    const totalZakat = sumOf("zakat");
    const netIncome = totalGross - totalPositionCost - totalBPJS;

    const ptkpAmount =
      getPTKPRate(marriageStatus.value, dependents?.value)?.amount || 0;

    // When income is annualized, Article 17 applies to the annualized PKP, so
    // the bracket breakdown has to use that same figure for the numbers to
    // reconcile.
    const annualisedTaxableIncome = yearMonths.reduce(
      (max, row) => Math.max(max, row.annualisedTaxableIncome || 0),
      0,
    );
    // UU PPh Pasal 17 ayat (4): the tariff runs on PKP floored to full
    // thousands. The tax figure already came out of that rounding, so the PKP
    // row and the bracket breakdown have to show the same base or the rows
    // stop summing to the total.
    const pkp = roundTaxableIncome(
      annualisedTaxableIncome > 0
        ? annualisedTaxableIncome
        : Math.max(0, netIncome - ptkpAmount),
    );

    return {
      annualisedTaxableIncome,
      totalGross,
      totalTax,
      totalTaxAllowance,
      totalPositionCost,
      totalBPJS,
      totalBpjsJHT,
      totalBpjsJP,
      totalPension,
      totalZakat,
      netIncome,
      monthCount: yearMonths.length,
      ptkpAmount,
      pkp,
    };
  }, [
    monthlyBreakdown,
    selectedYear,
    yearList,
    marriageStatus.value,
    dependents?.value,
  ]);

  // Calculations
  // Base calculations - income and allowances
  const baseCalculations = useMemo(() => {
    const {
      gross,
      allow,
      allowVariable,
      bonusAmount,
      thrAmount,
      overtimeAmount,
      premiumAmount,
      otherIncomeAmount,
    } = parsedInputs;

    const taxableAllowances =
      allow +
      allowVariable +
      bonusAmount +
      thrAmount +
      overtimeAmount +
      premiumAmount +
      otherIncomeAmount;

    const totalGrossIncome = gross + taxableAllowances;
    const isGrossUp = taxScheme.value === "GROSS_UP";
    const isYearlyCalculation =
      calculationPeriod.value === "yearly" && isPermanentOrRetired;

    // Separate one-time and monthly recurring amounts (only relevant for yearly + monthly input)
    const isMonthlyInput = inputMode.value === "monthly";
    let oneTimeTotal = 0;
    let monthlyAllowancesTotal = 0;
    if (isYearlyCalculation && isMonthlyInput) {
      const toggleableFields = {
        bonus: bonusAmount,
        thr: thrAmount,
        overtime: overtimeAmount,
      };
      // Allowances, premium, otherIncome are always monthly
      monthlyAllowancesTotal =
        allow + allowVariable + premiumAmount + otherIncomeAmount;
      Object.entries(toggleableFields).forEach(([key, amount]) => {
        if (oneTimeFields[key]) {
          oneTimeTotal += amount;
        } else {
          monthlyAllowancesTotal += amount;
        }
      });
    }

    return {
      gross,
      totalGrossIncome,
      taxableAllowances,
      isGrossUp,
      isYearlyCalculation,
      oneTimeTotal,
      monthlyRecurringIncome: gross + monthlyAllowancesTotal,
      // Upah for the BPJS employee contributions: gaji pokok plus tunjangan
      // tetap only. Kept apart from totalGrossIncome because biaya jabatan and
      // BPJS run on different bases.
      upah: gross + allow,
    };
  }, [
    parsedInputs,
    taxScheme.value,
    calculationPeriod.value,
    isPermanentOrRetired,
    inputMode.value,
    oneTimeFields,
  ]);

  // Income calculations based on period type
  const incomeCalculations = useMemo(() => {
    const {
      totalGrossIncome,
      isYearlyCalculation,
      isGrossUp,
      oneTimeTotal,
      monthlyRecurringIncome,
      upah,
    } = baseCalculations;

    let monthlyGrossIncome = totalGrossIncome;
    let yearlyGrossIncome = totalGrossIncome;

    if (isYearlyCalculation) {
      if (inputMode.value === "annual") {
        monthlyGrossIncome = totalGrossIncome / workMonths;
      } else {
        // For monthly input: recurring fields × workMonths + one-time fields once
        yearlyGrossIncome =
          oneTimeTotal > 0
            ? monthlyRecurringIncome * workMonths + oneTimeTotal
            : totalGrossIncome * workMonths;
      }
    }

    const incomeForCostCalc = isYearlyCalculation
      ? monthlyGrossIncome
      : totalGrossIncome;

    // Biaya jabatan takes penghasilan bruto, BPJS takes upah. Under annual
    // input both figures are typed for the whole period, so upah is brought
    // back to a month the same way monthlyGrossIncome is.
    const upahForContribution =
      isYearlyCalculation && inputMode.value === "annual"
        ? upah / workMonths
        : upah;

    const netSalaryInput =
      isGrossUp && isPermanentOrRetired ? totalGrossIncome : 0;

    return {
      monthlyGrossIncome,
      yearlyGrossIncome,
      incomeForCostCalc,
      upahForContribution,
      netSalaryInput,
    };
  }, [baseCalculations, inputMode.value, workMonths, isPermanentOrRetired]);

  // Position cost calculations
  const positionCostCalculations = useMemo(() => {
    const { isYearlyCalculation } = baseCalculations;
    const { yearlyGrossIncome, incomeForCostCalc } = incomeCalculations;

    const hasPositionCost = isPermanentOrRetired;
    const costPercentage =
      employeeType.value === "retired"
        ? PENSION_COST_PERCENTAGE
        : POSITION_COST_PERCENTAGE;
    const costMaxYearly =
      employeeType.value === "retired" ? PENSION_COST_MAX : POSITION_COST_MAX;

    const annualPositionCost = hasPositionCost
      ? isYearlyCalculation
        ? Math.min(yearlyGrossIncome * costPercentage, costMaxYearly)
        : Math.min(incomeForCostCalc * costPercentage, costMaxYearly / 12) * 12
      : 0;
    const positionCost = annualPositionCost / 12;
    const displayPositionCost = isYearlyCalculation
      ? annualPositionCost
      : positionCost;

    return {
      costPercentage,
      costMaxYearly,
      annualPositionCost,
      positionCost,
      displayPositionCost,
    };
  }, [
    baseCalculations,
    incomeCalculations,
    employeeType.value,
    isPermanentOrRetired,
  ]);

  // BPJS calculations
  const bpjsCalculations = useMemo(() => {
    const { upahForContribution } = incomeCalculations;
    // Only while the BPJS rows are on. A hand-entered amount left behind after
    // the box is unticked must not keep deducting.
    const hasBPJSDeduction = hasBPJS && employeeType.value === "permanent";

    // One month's figure, for the monthly period and the edit dialog. A
    // yearly period prices every month on its own in monthlyBreakdown; this
    // takes the first month it covers so the two start from the same cap.
    const { bpjsJHT: defaultBpjsJHT, bpjsJP: defaultBpjsJP } = calculateBPJS(
      upahForContribution,
      bpjsPricingDate,
    );

    const bpjsJHT = hasBPJSDeduction
      ? customBpjsJHT !== null
        ? customBpjsJHT
        : defaultBpjsJHT
      : 0;
    const bpjsJP = hasBPJSDeduction
      ? customBpjsJP !== null
        ? customBpjsJP
        : defaultBpjsJP
      : 0;
    const pensionAmount =
      hasPensionContribution && customPensionAmount ? customPensionAmount : 0;
    const zakatAmount = hasZakat && customZakatAmount ? customZakatAmount : 0;
    const totalBPJS = bpjsJHT + bpjsJP + pensionAmount + zakatAmount;
    const annualBPJS = totalBPJS * 12;

    return {
      bpjsJHT,
      bpjsJP,
      pensionAmount,
      zakatAmount,
      totalBPJS,
      annualBPJS,
    };
  }, [
    incomeCalculations,
    hasBPJS,
    employeeType.value,
    bpjsPricingDate,
    calculateBPJS,
    customBpjsJHT,
    customBpjsJP,
    hasPensionContribution,
    customPensionAmount,
    hasZakat,
    customZakatAmount,
  ]);

  // Tax calculations for permanent/retired employees
  // BPJS, pension and zakat over the whole period, month by month. Null when
  // there is no monthly breakdown to sum.
  const periodBpjsTotal = useMemo(
    () =>
      monthlyBreakdown
        ? monthlyBreakdown.months.reduce((sum, row) => sum + row.bpjs, 0)
        : null,
    [monthlyBreakdown],
  );

  const permanentTaxCalculations = useMemo(() => {
    if (!isPermanentOrRetired) return null;

    const {
      totalGrossIncome,
      taxableAllowances,
      isGrossUp,
      isYearlyCalculation,
      oneTimeTotal,
      monthlyRecurringIncome,
    } = baseCalculations;
    const { yearlyGrossIncome, netSalaryInput } = incomeCalculations;
    const { costPercentage, costMaxYearly } = positionCostCalculations;
    const { totalBPJS } = bpjsCalculations;

    const terCategory = getTERCategory(marriageStatus.value, dependents?.value);
    const ptkpAmount =
      getPTKPRate(marriageStatus.value, dependents?.value)?.amount || 0;

    let pph21 = 0,
      pph21Monthly = 0,
      pph21Yearly = 0,
      pkp = 0,
      terRate = 0,
      annualizedNetIncome = null,
      finalTotalGrossIncome = totalGrossIncome,
      finalYearlyGrossIncome = yearlyGrossIncome;

    if (isGrossUp && !isYearlyCalculation) {
      const calculatedGrossSalary = calculateGrossFromNet(
        netSalaryInput - taxableAllowances,
        taxableAllowances,
        terCategory,
      );
      finalTotalGrossIncome = calculatedGrossSalary;
      terRate = calculateTERRate(calculatedGrossSalary, terCategory);
      pph21 = calculatedGrossSalary * terRate;
      pph21Monthly = pph21;
    } else if (isYearlyCalculation) {
      // Determine the total gross for the period based on input mode
      const isAnnualInput = inputMode.value === "annual";
      const hasOneTimeFields = !isAnnualInput && oneTimeTotal > 0;
      const totalGrossForPeriod = isAnnualInput
        ? totalGrossIncome
        : hasOneTimeFields
          ? monthlyRecurringIncome * workMonths + oneTimeTotal
          : totalGrossIncome * workMonths;
      const monthlyGrossForCalc = isAnnualInput
        ? totalGrossIncome / workMonths
        : hasOneTimeFields
          ? monthlyRecurringIncome
          : totalGrossIncome;

      if (calculationMethod.value === "annualized") {
        // DISETAHUNKAN: Annualize monthly income to 12 months, calculate tax, then de-annualize
        const annualizedGross = monthlyGrossForCalc * 12;
        const annualizedPositionCost = Math.min(
          annualizedGross * costPercentage,
          costMaxYearly,
        );
        const annualizedBPJS = totalBPJS * 12;

        // UU PPh Pasal 17 ayat (4): the tariff runs on PKP floored to full
        // thousands.
        const annualizedPKP = roundTaxableIncome(
          Math.max(
            0,
            annualizedGross -
              annualizedPositionCost -
              annualizedBPJS -
              ptkpAmount,
          ),
        );
        const annualizedTax = calculateProgressiveTax(annualizedPKP);

        // De-annualize: prorate tax back to actual work months
        pph21Yearly = (annualizedTax / 12) * workMonths;
        pph21 = pph21Yearly;
        pph21Monthly = annualizedTax / 12;
        pkp = annualizedPKP;
        annualizedNetIncome =
          annualizedGross - annualizedPositionCost - annualizedBPJS;
        finalYearlyGrossIncome = annualizedGross;
        finalTotalGrossIncome = totalGrossForPeriod;
      } else {
        // SETAHUN PENUH & KURANG DARI SETAHUN: Use actual income for the period
        const maxPositionCostForPeriod =
          workMonths < 12 ? (costMaxYearly / 12) * workMonths : costMaxYearly;
        const actualPositionCost = Math.min(
          totalGrossForPeriod * costPercentage,
          maxPositionCostForPeriod,
        );
        // The months' own figures, each priced at the cap in force then. One
        // month times workMonths cannot be right for a period that crosses a
        // cap change, and it is what the deduction rows already show.
        const cumulativeBPJSTotal = periodBpjsTotal ?? totalBPJS * workMonths;

        pkp = roundTaxableIncome(
          Math.max(
            0,
            totalGrossForPeriod -
              actualPositionCost -
              cumulativeBPJSTotal -
              ptkpAmount,
          ),
        );
        const totalProgressiveTax = calculateProgressiveTax(pkp);

        pph21Yearly = totalProgressiveTax;
        pph21 = pph21Yearly;
        pph21Monthly = pph21Yearly / workMonths;
        finalTotalGrossIncome = totalGrossForPeriod;
        finalYearlyGrossIncome = totalGrossForPeriod;
      }
    } else {
      terRate = calculateTERRate(totalGrossIncome, terCategory);
      pph21 = totalGrossIncome * terRate;
      pph21Monthly = pph21;
    }

    return {
      terCategory,
      terRate,
      ptkpAmount,
      pkp,
      pph21,
      pph21Monthly,
      pph21Yearly,
      annualizedNetIncome,
      finalTotalGrossIncome,
      finalYearlyGrossIncome,
    };
  }, [
    periodBpjsTotal,
    isPermanentOrRetired,
    baseCalculations,
    incomeCalculations,
    positionCostCalculations,
    bpjsCalculations,
    marriageStatus.value,
    dependents?.value,
    calculationMethod.value,
    inputMode.value,
    workMonths,
  ]);

  // Tax calculations for non-permanent employees
  const nonPermanentTaxCalculations = useMemo(() => {
    if (employeeType.value !== "non-permanent") return null;

    const { totalGrossIncome } = baseCalculations;
    const parsedWorkingDays = parseInt(workingDays) || 0;

    let terCategory = "A",
      terRate = 0,
      pph21 = 0,
      pkp = 0,
      ptkpAmount = 0,
      dailyCalculationResult = null;

    if (isMonthly) {
      const nonPermanentTerCategory = getTERCategory(
        marriageStatus.value,
        dependents?.value,
      );
      const monthlyTerRate = calculateTERRate(
        totalGrossIncome,
        nonPermanentTerCategory,
      );
      pph21 = totalGrossIncome * monthlyTerRate;
      terRate = monthlyTerRate;
      terCategory = nonPermanentTerCategory;
    } else {
      const dailyGross = totalGrossIncome;
      const days = parsedWorkingDays;
      const dailyResult = calculatePPh21NonPermanentDaily(
        dailyGross,
        days,
        marriageStatus.value,
        dependents?.value,
      );
      dailyCalculationResult = dailyResult;
      pph21 = dailyResult.totalTax;
      pkp = dailyResult.pkp;
      ptkpAmount = dailyResult.monthlyPtkp;

      if (dailyResult.method === "TER_DAILY") {
        terRate = dailyResult.terRate;
      } else {
        // Effective rate on the full daily wage, for the summary line. The
        // panel above shows the bracket-by-bracket figures.
        terRate = dailyGross > 0 ? dailyResult.dailyTax / dailyGross : 0;
      }
    }

    return {
      terCategory,
      terRate,
      ptkpAmount,
      pkp,
      pph21,
      dailyCalculationResult,
    };
  }, [
    employeeType.value,
    baseCalculations,
    isMonthly,
    workingDays,
    marriageStatus.value,
    dependents?.value,
  ]);

  // Tax calculations for other employee types (freelance, progressive types, non-resident)
  const otherTaxCalculations = useMemo(() => {
    const { totalGrossIncome } = baseCalculations;

    if (employeeType.value === "freelance") {
      const dpp = totalGrossIncome * 0.5;
      return { pph21: calculateProgressiveTax(dpp), pkp: dpp };
    }

    if (PROGRESSIVE_TAX_TYPES.includes(employeeType.value)) {
      return {
        pph21: calculateProgressiveTax(totalGrossIncome),
        pkp: totalGrossIncome,
      };
    }

    if (employeeType.value === "non-resident") {
      return { pph21: totalGrossIncome * (pph26Rate / 100), pkp: 0 };
    }

    return null;
  }, [baseCalculations, employeeType.value, pph26Rate]);

  // Final calculations - combine all results
  const calculations = useMemo(() => {
    const { totalGrossIncome, isYearlyCalculation } = baseCalculations;
    const { monthlyGrossIncome, yearlyGrossIncome } = incomeCalculations;
    const { costPercentage, costMaxYearly, displayPositionCost } =
      positionCostCalculations;
    const { bpjsJHT, bpjsJP, totalBPJS } = bpjsCalculations;

    let terCategory = "A",
      terRate = 0,
      pph21 = 0,
      pph21Monthly = 0,
      pph21Yearly = 0,
      pkp = 0,
      ptkpAmount = 0,
      dailyCalculationResult = null,
      finalTotalGrossIncome = totalGrossIncome,
      finalYearlyGrossIncome = yearlyGrossIncome;

    let annualizedNetIncome = null;

    if (permanentTaxCalculations) {
      terCategory = permanentTaxCalculations.terCategory;
      terRate = permanentTaxCalculations.terRate;
      ptkpAmount = permanentTaxCalculations.ptkpAmount;
      pkp = permanentTaxCalculations.pkp;
      pph21 = permanentTaxCalculations.pph21;
      pph21Monthly = permanentTaxCalculations.pph21Monthly;
      pph21Yearly = permanentTaxCalculations.pph21Yearly;
      annualizedNetIncome = permanentTaxCalculations.annualizedNetIncome;
      finalTotalGrossIncome = permanentTaxCalculations.finalTotalGrossIncome;
      finalYearlyGrossIncome = permanentTaxCalculations.finalYearlyGrossIncome;
    } else if (nonPermanentTaxCalculations) {
      terCategory = nonPermanentTaxCalculations.terCategory;
      terRate = nonPermanentTaxCalculations.terRate;
      ptkpAmount = nonPermanentTaxCalculations.ptkpAmount;
      pkp = nonPermanentTaxCalculations.pkp;
      pph21 = nonPermanentTaxCalculations.pph21;
      dailyCalculationResult =
        nonPermanentTaxCalculations.dailyCalculationResult;
    } else if (otherTaxCalculations) {
      pph21 = otherTaxCalculations.pph21;
      pkp = otherTaxCalculations.pkp;
    }

    let displayTotalGross = finalTotalGrossIncome;
    let displayBPJS = totalBPJS;
    let displayPph21 = pph21;
    let displayPositionCostFinal = displayPositionCost;

    if (isYearlyCalculation) {
      displayBPJS = periodBpjsTotal ?? totalBPJS * workMonths;
      displayPph21 = pph21Yearly;
      displayTotalGross = finalTotalGrossIncome;

      const maxPositionCostForPeriod =
        calculationMethod.value === "annualized"
          ? costMaxYearly
          : (costMaxYearly / 12) * workMonths;
      displayPositionCostFinal = Math.min(
        (calculationMethod.value === "annualized"
          ? monthlyGrossIncome * 12
          : finalTotalGrossIncome) * costPercentage,
        maxPositionCostForPeriod,
      );
    }

    // Net income has to be the gross the panel shows minus the deductions the
    // panel shows. Deriving it from the full-year figures instead left the
    // three rows unable to produce one another.
    const displayNetIncome =
      displayTotalGross - displayPositionCostFinal - displayBPJS;

    return {
      totalGrossIncome: displayTotalGross,
      positionCost: displayPositionCostFinal,
      bpjsJHT,
      bpjsJP,
      totalBPJS: displayBPJS,
      netIncome: displayNetIncome,
      terCategory,
      terRate: isYearlyCalculation ? terRate : terRate * 100,
      pph21: displayPph21,
      isYearlyCalculation,
      calculationMethod: isYearlyCalculation ? calculationMethod.value : null,
      inputMode: isYearlyCalculation ? inputMode.value : null,
      monthlyGrossIncome,
      yearlyGrossIncome: finalYearlyGrossIncome,
      pph21Monthly,
      pph21Yearly,
      pkp,
      ptkpAmount,
      annualizedNetIncome,
      dailyCalculationResult,
    };
  }, [
    periodBpjsTotal,
    baseCalculations,
    incomeCalculations,
    positionCostCalculations,
    bpjsCalculations,
    permanentTaxCalculations,
    nonPermanentTaxCalculations,
    otherTaxCalculations,
    workMonths,
    calculationMethod.value,
    inputMode.value,
  ]);

  // The annualized rows in the results panel must come from the same engine as
  // the bracket breakdown. The older summary does not know about the tax
  // allowance, so under gross-up its figures do not line up.
  const annualisedPkp = yearlyCalculations
    ? yearlyCalculations.pkp
    : calculations.pkp;
  const annualisedPtkpAmount = yearlyCalculations
    ? yearlyCalculations.ptkpAmount
    : calculations.ptkpAmount;
  const annualisedNetIncome = annualisedPkp + annualisedPtkpAmount;

  useEffect(() => {
    handleReset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (calculationMethod.value === "full_year") {
      setStartDate(null);
      setEndDate(null);
    }
  }, [calculationMethod.value]);

  const handleReset = () => {
    setGrossSalary("");
    setAllowances("");
    setVariableAllowances("");
    setBonus("");
    setThr("");
    setOvertime("");
    setPremium("");
    setOtherIncome("");
    setMarriageStatus(MARRIAGE_STATUS_OPTIONS[0]);
    setDependents(null);
    setIsMonthly(true);
    setWorkingDays("");
    setWageType(WAGE_TYPE_OPTIONS[0]);
    setContractTotal("");
    setHasBPJS(employeeType.value === "permanent");
    setHasPensionContribution(false);
    setHasZakat(false);
    setTaxScheme(TAX_METHOD_OPTIONS[0]);
    setCalculationPeriod(CALCULATION_PERIOD_OPTIONS[0]);
    setCalculationMethod(CALCULATION_METHOD_OPTIONS[0]);
    setInputMode(INPUT_MODE_OPTIONS[0]);
    setStartDate(null);
    setEndDate(null);
    setCustomBpjsJHT(null);
    setCustomBpjsJP(null);
    setPph26Rate(20);
    setOneTimeFields({ bonus: true, thr: true, overtime: false });
  };

  const handleOpenBPJSModal = (type) => {
    let currentAmount = 0;
    if (type === "JHT") {
      currentAmount = calculations.bpjsJHT;
      setBpjsEntryMode(customBpjsJHT !== null ? "manual" : "automatic");
    } else if (type === "JP") {
      currentAmount = calculations.bpjsJP;
      setBpjsEntryMode(customBpjsJP !== null ? "manual" : "automatic");
    } else if (type === "PENSION") {
      currentAmount = customPensionAmount || 0;
    } else if (type === "ZAKAT") {
      currentAmount = customZakatAmount || 0;
    } else if (type === "PPH26_RATE") {
      currentAmount = pph26Rate;
    }
    setModalInputMode("monthly");
    setTempAmount(currentAmount.toString());
    setEditingBPJSType(type);
  };

  const handleSaveBPJSAmount = () => {
    const isBPJSJHTOrJP = ["JHT", "JP"].includes(editingBPJSType);

    if (isBPJSJHTOrJP && bpjsEntryMode === "automatic") {
      if (editingBPJSType === "JHT") {
        setCustomBpjsJHT(null);
      } else if (editingBPJSType === "JP") {
        setCustomBpjsJP(null);
      }
      setEditingBPJSType(null);
      return;
    }

    const amount = parseCurrencyValue(tempAmount);
    const monthCount = yearlyCalculations
      ? yearlyCalculations.monthCount
      : workMonths;
    const monthlyAmount =
      modalInputMode === "yearly" ? amount / (monthCount || 12) : amount;
    if (editingBPJSType === "JHT") {
      setCustomBpjsJHT(monthlyAmount);
    } else if (editingBPJSType === "JP") {
      setCustomBpjsJP(monthlyAmount);
    } else if (editingBPJSType === "PENSION") {
      setCustomPensionAmount(monthlyAmount);
    } else if (editingBPJSType === "ZAKAT") {
      setCustomZakatAmount(monthlyAmount);
    } else if (editingBPJSType === "PPH26_RATE") {
      const rate = parseFloat(tempAmount) || 0;
      setPph26Rate(Math.min(20, Math.max(0, rate)));
    }
    setEditingBPJSType(null);
  };

  const getFieldLabel = (baseLabel) => {
    const isGrossUp = taxScheme.value === "GROSS_UP";

    let translationKey = `taxCalculator.income.${baseLabel}`;
    if (employeeType.value === "retired" && baseLabel === "grossSalary") {
      translationKey = "taxCalculator.income.pensionFund";
    }

    if (employeeType.value === "non-permanent" && baseLabel === "grossSalary") {
      translationKey = isMonthly
        ? "taxCalculator.nonPermanent.monthlyGross"
        : "taxCalculator.nonPermanent.wageType.daily";
    }

    if (employeeType.value === "permanent" && baseLabel === "allowances") {
      translationKey = "taxCalculator.income.fixedAllowances";
    }

    if (employeeType.value === "freelance" && baseLabel === "grossSalary") {
      translationKey = "taxCalculator.income.honorarium";
    }

    if (
      employeeType.value === "activity-participant" &&
      baseLabel === "grossSalary"
    ) {
      translationKey = "taxCalculator.income.rewardOrCompensation";
    }

    if (
      employeeType.value === "pension-withdrawal" &&
      baseLabel === "grossSalary"
    ) {
      translationKey = "taxCalculator.income.pensionBenefitWithdrawal";
    }

    if (
      FORMER_EMPLOYEE_TYPES.includes(employeeType.value) &&
      baseLabel === "grossSalary"
    ) {
      translationKey = "taxCalculator.income.formerEmployeeIncome";
    }

    let label = t(translationKey);
    let suffix = "";

    // Add period indicator to the label for income fields (only for permanent/retired employees with yearly calculation)
    const incomeFields = [
      "grossSalary",
      "allowances",
      "variableAllowances",
      "bonus",
      "thr",
      "overtime",
      "otherIncome",
      "insurancePremium",
    ];
    const oneTimeEligibleFields = ["bonus", "thr", "overtime"];
    if (
      incomeFields.includes(baseLabel) &&
      calculationPeriod.value === "yearly" &&
      isPermanentOrRetired
    ) {
      if (inputMode.value === "annual") {
        suffix = ` (${t("taxCalculator.periodType.yearly")})`;
      } else if (
        oneTimeEligibleFields.includes(baseLabel) &&
        oneTimeFields[baseLabel]
      ) {
        suffix = ` (${t("taxCalculator.periodType.oneTime")})`;
      } else {
        suffix = ` (${t("taxCalculator.periodType.monthly")})`;
      }
    }

    if (isGrossUp && isPermanentOrRetired) {
      label = `${label} ${t("taxCalculator.taxScheme.net")}`;
    }

    return label + suffix;
  };

  const showOneTimeToggle =
    calculationPeriod.value === "yearly" &&
    inputMode.value === "monthly" &&
    isPermanentOrRetired;

  // The toggle appears and disappears with the input mode, so it is drawn
  // inside the label's own line box and given its hit area by a pseudo-element.
  // A toggle that added height would shift the field in the neighbouring column
  // every time the input mode changed.
  const renderIncomeField = (
    fieldKey,
    value,
    setter,
    { oneTimeToggle = true } = {},
  ) => {
    const isOneTime = oneTimeFields[fieldKey];
    return (
      <div>
        <FormLabel
          action={
            oneTimeToggle &&
            showOneTimeToggle && (
              <button
                type="button"
                onClick={() =>
                  setOneTimeFields((prev) => ({
                    ...prev,
                    [fieldKey]: !prev[fieldKey],
                  }))
                }
                className={QUIET_LINK_CLASS}
              >
                {isOneTime
                  ? t("taxCalculator.periodType.switchToMonthly")
                  : t("taxCalculator.periodType.switchToOneTime")}
              </button>
            )
          }
        >
          {getFieldLabel(fieldKey)}
        </FormLabel>
        <div className="mt-1">
          <FormInput
            type="text"
            aria-label={getFieldLabel(fieldKey)}
            inputMode="decimal"
            value={value}
            onChange={(e) => setter(e.target.value)}
            placeholder="0"
            numberFormat={{
              thousandSeparator: ".",
              decimalSeparator: ",",
              allowNegative: false,
              prefix: "Rp ",
            }}
          />
        </div>
      </div>
    );
  };

  const renderModalBPJSEdit = () => {
    const getModalTitle = () => {
      if (editingBPJSType === "JHT") return "taxCalculator.bpjs.editJHT";
      if (editingBPJSType === "JP") return "taxCalculator.bpjs.editJP";
      if (editingBPJSType === "PENSION")
        return "taxCalculator.pension.editAmount";
      if (editingBPJSType === "ZAKAT") return "taxCalculator.zakat.editAmount";
      if (editingBPJSType === "PPH26_RATE")
        return t("taxCalculator.pph26.editRate");
      return "";
    };

    const isBPJSJHTOrJP = ["JHT", "JP"].includes(editingBPJSType);
    const isBPJSType = ["JHT", "JP", "PENSION", "ZAKAT"].includes(
      editingBPJSType,
    );
    const isYearlyCalc = calculations?.isYearlyCalculation;
    const monthCount = yearlyCalculations
      ? yearlyCalculations.monthCount
      : workMonths;

    const handleModeSwitch = (newMode) => {
      const currentValue = parseCurrencyValue(tempAmount);
      if (newMode === "yearly" && modalInputMode === "monthly") {
        setTempAmount((currentValue * (monthCount || 12)).toString());
      } else if (newMode === "monthly" && modalInputMode === "yearly") {
        setTempAmount((currentValue / (monthCount || 12)).toString());
      }
      setModalInputMode(newMode);
    };

    const getAutomaticBreakdown = () => {
      const { incomeForCostCalc } = incomeCalculations;
      if (editingBPJSType === "JHT") {
        const config = CONTRIBUTION_CONFIGS.BPJS_KETENAGAKERJAAN_JHT;
        const employeeAmount = incomeForCostCalc * config.percentage;
        const employerAmount = incomeForCostCalc * config.employerPercentage;
        return {
          employeeAmount,
          employerAmount,
          employeeRate: `${(config.percentage * 100).toFixed(1)}%`,
          employerRate: `${(config.employerPercentage * 100).toFixed(1)}%`,
        };
      }
      if (editingBPJSType === "JP") {
        const config = CONTRIBUTION_CONFIGS.BPJS_KETENAGAKERJAAN_JP;
        const jpEmployee = incomeForCostCalc * config.percentage;
        const jpEmployer = incomeForCostCalc * config.employerPercentage;
        const maxAmount = getContributionMaxAmount(config, bpjsPricingDate);
        const employeeAmount = Math.min(jpEmployee, maxAmount);
        const employerAmount = Math.min(
          jpEmployer,
          maxAmount * (config.employerPercentage / config.percentage),
        );
        return {
          employeeAmount,
          employerAmount,
          employeeRate: `${(config.percentage * 100).toFixed(0)}%`,
          employerRate: `${(config.employerPercentage * 100).toFixed(0)}%`,
        };
      }
      return null;
    };

    const entryModeOptions = [
      { label: "taxCalculator.bpjs.automatic", value: "automatic" },
      { label: "taxCalculator.bpjs.manual", value: "manual" },
    ];

    return (
      <Modal
        show={!!editingBPJSType}
        onClose={() => setEditingBPJSType(null)}
        title={getModalTitle()}
        footer={
          <div className="flex justify-between p-4 border-t">
            <Button
              onClick={() => setEditingBPJSType(null)}
              title={t("common.cancel")}
              outline
            />
            <Button onClick={handleSaveBPJSAmount} title={t("common.save")} />
          </div>
        }
      >
        <div className="p-4 space-y-4">
          {isBPJSJHTOrJP && (
            <FormRadioGroup
              variant="segmented"
              label="taxCalculator.bpjs.inputMethod"
              options={entryModeOptions}
              value={entryModeOptions.find((o) => o.value === bpjsEntryMode)}
              onChange={(option) => {
                setBpjsEntryMode(option.value);
                if (option.value === "automatic") {
                  const breakdown = getAutomaticBreakdown();
                  if (breakdown) {
                    setTempAmount(breakdown.employeeAmount.toString());
                  }
                }
              }}
              verticalSpace="py-2"
            />
          )}

          {isBPJSJHTOrJP && bpjsEntryMode === "automatic" ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                {(() => {
                  const breakdown = getAutomaticBreakdown();
                  if (!breakdown) return null;
                  return (
                    <>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-slate-700">
                            {t("taxCalculator.bpjs.employeePortion")}
                          </p>
                          <p className="text-xs text-slate-500">
                            {breakdown.employeeRate} ×{" "}
                            {formatMoney(incomeCalculations.incomeForCostCalc)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">
                            {formatMoney(breakdown.employeeAmount)}
                            <span className="text-xs text-slate-500 font-normal ml-1">
                              /{t("month")}
                            </span>
                          </p>
                          {isYearlyCalc && (
                            <p className="text-xs text-slate-500">
                              {formatMoney(
                                breakdown.employeeAmount * (monthCount || 12),
                              )}
                              <span className="ml-1">
                                /{monthCount || 12} {t("month")}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="border-t border-slate-200" />
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-slate-700">
                            {t("taxCalculator.bpjs.employerPortion")}
                          </p>
                          <p className="text-xs text-slate-500">
                            {breakdown.employerRate} ×{" "}
                            {formatMoney(incomeCalculations.incomeForCostCalc)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">
                            {formatMoney(breakdown.employerAmount)}
                            <span className="text-xs text-slate-500 font-normal ml-1">
                              /{t("month")}
                            </span>
                          </p>
                          {isYearlyCalc && (
                            <p className="text-xs text-slate-500">
                              {formatMoney(
                                breakdown.employerAmount * (monthCount || 12),
                              )}
                              <span className="ml-1">
                                /{monthCount || 12} {t("month")}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="border-t border-slate-200" />
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-slate-700">
                          {t("taxCalculator.bpjs.total")}
                        </p>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">
                            {formatMoney(
                              breakdown.employeeAmount +
                                breakdown.employerAmount,
                            )}
                            <span className="text-xs text-slate-500 font-normal ml-1">
                              /{t("month")}
                            </span>
                          </p>
                          {isYearlyCalc && (
                            <p className="text-xs text-slate-500">
                              {formatMoney(
                                (breakdown.employeeAmount +
                                  breakdown.employerAmount) *
                                  (monthCount || 12),
                              )}
                              <span className="ml-1">
                                /{monthCount || 12} {t("month")}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <p className="text-xs text-slate-500">
                {t("taxCalculator.bpjs.automaticHint")}
              </p>
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {editingBPJSType === "JHT" &&
                    t("taxCalculator.results.bpjsJHT")}
                  {editingBPJSType === "JP" &&
                    t("taxCalculator.results.bpjsJP")}
                  {editingBPJSType === "PENSION" &&
                    t("taxCalculator.pension.contribution")}
                  {editingBPJSType === "ZAKAT" &&
                    t("taxCalculator.zakat.label")}
                  {editingBPJSType === "PPH26_RATE" &&
                    t("taxCalculator.pph26.rate")}
                  {isBPJSType && (
                    <span className="text-xs text-gray-500 font-normal ml-1">
                      (
                      {modalInputMode === "yearly"
                        ? t("taxCalculator.periodType.yearly")
                        : `/${t("month")}`}
                      )
                    </span>
                  )}
                </label>
                {isBPJSType && isYearlyCalc && (
                  <button
                    type="button"
                    onClick={() =>
                      handleModeSwitch(
                        modalInputMode === "monthly" ? "yearly" : "monthly",
                      )
                    }
                    className="min-h-[32px] rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 underline decoration-slate-300 underline-offset-2 transition-colors hover:text-slate-900 hover:decoration-slate-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  >
                    {t("taxCalculator.changeTo")}{" "}
                    {modalInputMode === "monthly"
                      ? t("taxCalculator.periodType.yearly")
                      : `/${t("month")}`}
                  </button>
                )}
              </div>
              {editingBPJSType === "PPH26_RATE" ? (
                <>
                  <FormInput
                    type="number"
                    value={tempAmount}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (
                        e.target.value === "" ||
                        (value >= 0 && value <= 20)
                      ) {
                        setTempAmount(e.target.value);
                      }
                    }}
                    prefix={"%"}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {t("taxCalculator.pph26.rateHint")}
                  </p>
                </>
              ) : (
                <FormInput
                  type="text"
                  value={tempAmount}
                  onChange={(e) => setTempAmount(e.target.value)}
                  placeholder="0"
                  numberFormat={{
                    thousandSeparator: ".",
                    decimalSeparator: ",",
                    allowNegative: false,
                    prefix: "Rp ",
                  }}
                />
              )}
            </div>
          )}
        </div>
      </Modal>
    );
  };

  const renderMonthlyBreakdownTable = () => {
    if (!monthlyBreakdown || !calculations.isYearlyCalculation) return null;

    const filteredMonths = selectedYear
      ? monthlyBreakdown.months.filter((row) => row.year === selectedYear)
      : monthlyBreakdown.months;

    const filteredTotalGross = filteredMonths.reduce(
      (sum, row) => sum + row.grossIncome,
      0,
    );
    const filteredTotalTax = filteredMonths.reduce(
      (sum, row) => sum + row.pph21,
      0,
    );

    return (
      <div
        id={MONTHLY_BREAKDOWN_ID}
        className="bg-white rounded-card border border-slate-200/60 shadow-sm overflow-hidden scroll-mt-24"
      >
        <div className="flex flex-wrap justify-between items-center gap-3 px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">
            {t("taxCalculator.monthlyBreakdown.title")}
          </h3>

          {yearList.length > 0 && (
            <div className="w-40">
              <FormSelect
                label=""
                value={{
                  value: selectedYear,
                  label: selectedYear?.toString() || "",
                }}
                onChange={(option) => setSelectedYear(option.value)}
                options={yearList}
                isDisabled={yearList.length === 1}
              />
            </div>
          )}
        </div>
        {/* Annual inputs carry no month, so every row is 1/12 of the year.
            THR, bonus and overtime really land in one month and push that
            month's TER up, so only the yearly total can be relied on. */}
        {inputMode.value === "annual" && (
          <p className="px-5 py-3 text-xs text-amber-800 bg-amber-50 border-b border-amber-100">
            {t("taxCalculator.monthlyBreakdown.annualSplitNote")}
          </p>
        )}
        <div
          className="overflow-x-auto focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
          tabIndex={0}
          role="region"
          aria-label={t("taxCalculator.monthlyBreakdown.title")}
        >
          <table className="min-w-full divide-y divide-slate-100 tabular-nums">
            <thead className="bg-slate-50">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider text-nowrap"
                >
                  {t("month")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider text-nowrap"
                >
                  {t("taxCalculator.monthlyBreakdown.grossIncome")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider text-nowrap"
                >
                  {t("taxCalculator.monthlyBreakdown.method")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider text-nowrap"
                >
                  {t("taxCalculator.results.terCategory")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider text-nowrap"
                >
                  {t("taxCalculator.results.terRate")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider text-nowrap"
                >
                  {t("taxCalculator.results.pph21")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {filteredMonths.map((row, index) => (
                <tr
                  key={`${row.year}-${row.month}-${index}`}
                  className={row.method === "Progressive" ? "bg-yellow-50" : ""}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                    {t(row.monthName)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 text-right">
                    {formatMoney(row.grossIncome)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        row.method === "TER"
                          ? "bg-slate-100 text-slate-700"
                          : "bg-blue-50 text-blue-700"
                      }`}
                    >
                      {row.method === "TER"
                        ? "TER"
                        : t("taxCalculator.monthlyBreakdown.progressive")}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-medium text-slate-700">
                    {row.method === "TER" ? monthlyBreakdown.terCategory : "-"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 text-right">
                    {row.method === "TER" ? `${row.terRate.toFixed(2)}%` : "-"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900 text-right">
                    {formatMoney(row.pph21)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-900">
                  {t("Total")}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-900 text-right">
                  {formatMoney(filteredTotalGross)}
                </td>
                <td colSpan={3}></td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-900 text-right">
                  {formatMoney(filteredTotalTax)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Legend */}
        <div className="px-5 py-4 flex flex-wrap gap-6 text-xs border-t border-slate-100">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 font-medium">
              TER
            </span>
            <span className="text-slate-600">
              {t("taxCalculator.monthlyBreakdown.terMethodDescription")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 font-medium">
              {t("taxCalculator.monthlyBreakdown.progressive")}
            </span>
            <span className="text-slate-600">
              {t("taxCalculator.monthlyBreakdown.progressiveMethodDescription")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const handleSelectExample = (exampleData) => {
    const empType = EMPLOYEE_TYPE_OPTIONS.find(
      (opt) => opt.value === exampleData.employeeType,
    );
    if (empType) {
      setEmployeeType(empType);
    }

    const calcPeriod = exampleData.calculationPeriod
      ? CALCULATION_PERIOD_OPTIONS.find(
          (opt) => opt.value === exampleData.calculationPeriod,
        )
      : CALCULATION_PERIOD_OPTIONS[0];
    setCalculationPeriod(calcPeriod || CALCULATION_PERIOD_OPTIONS[0]);

    const calcMethod = exampleData.calculationMethod
      ? CALCULATION_METHOD_OPTIONS.find(
          (opt) => opt.value === exampleData.calculationMethod,
        )
      : CALCULATION_METHOD_OPTIONS[0];
    setCalculationMethod(calcMethod || CALCULATION_METHOD_OPTIONS[0]);

    const iMode = exampleData.inputMode
      ? INPUT_MODE_OPTIONS.find((opt) => opt.value === exampleData.inputMode)
      : INPUT_MODE_OPTIONS[0];
    setInputMode(iMode || INPUT_MODE_OPTIONS[0]);

    const scheme = exampleData.taxScheme
      ? TAX_METHOD_OPTIONS.find((opt) => opt.value === exampleData.taxScheme)
      : TAX_METHOD_OPTIONS[0];
    setTaxScheme(scheme || TAX_METHOD_OPTIONS[0]);

    setGrossSalary(
      exampleData.grossSalary ? formatMoney(exampleData.grossSalary) : "",
    );
    setAllowances(
      exampleData.allowances ? formatMoney(exampleData.allowances) : "",
    );
    // Examples written before the tunjangan tetap / tidak tetap split carry a
    // single `allowances` figure. Leaving it on the fixed side keeps their
    // penghasilan bruto identical and matches the recurring monthly allowance
    // those examples describe.
    setVariableAllowances(
      exampleData.variableAllowances
        ? formatMoney(exampleData.variableAllowances)
        : "",
    );
    setBonus(exampleData.bonus ? formatMoney(exampleData.bonus) : "");
    setThr(exampleData.thr ? formatMoney(exampleData.thr) : "");
    setOvertime(exampleData.overtime ? formatMoney(exampleData.overtime) : "");
    setPremium(exampleData.premium ? formatMoney(exampleData.premium) : "");
    setOtherIncome(
      exampleData.otherIncome ? formatMoney(exampleData.otherIncome) : "",
    );

    const marStatus = exampleData.marriageStatus
      ? MARRIAGE_STATUS_OPTIONS.find(
          (opt) => opt.value === exampleData.marriageStatus,
        )
      : MARRIAGE_STATUS_OPTIONS[0];
    setMarriageStatus(marStatus || MARRIAGE_STATUS_OPTIONS[0]);

    const depOption =
      exampleData.dependents !== undefined
        ? DEPENDENT_OPTIONS.find((opt) => opt.value === exampleData.dependents)
        : DEPENDENT_OPTIONS[0];
    setDependents(depOption || DEPENDENT_OPTIONS[0]);

    setHasBPJS(exampleData.hasBPJS !== undefined ? exampleData.hasBPJS : false);
    setHasPensionContribution(
      exampleData.hasPensionContribution !== undefined
        ? exampleData.hasPensionContribution
        : false,
    );
    setHasZakat(
      exampleData.hasZakat !== undefined ? exampleData.hasZakat : false,
    );
    setCustomPensionAmount(exampleData.customPensionAmount || null);
    setCustomZakatAmount(exampleData.customZakatAmount || null);

    setIsMonthly(
      exampleData.isMonthly !== undefined ? exampleData.isMonthly : true,
    );
    const wType = exampleData.wageType
      ? WAGE_TYPE_OPTIONS.find((opt) => opt.value === exampleData.wageType)
      : WAGE_TYPE_OPTIONS[0];
    setWageType(wType || WAGE_TYPE_OPTIONS[0]);
    setWorkingDays(
      exampleData.workingDays ? exampleData.workingDays.toString() : "",
    );
    setContractTotal(
      exampleData.contractTotal ? formatMoney(exampleData.contractTotal) : "",
    );

    setStartDate(
      exampleData.startDate ? new Date(exampleData.startDate) : null,
    );
    setEndDate(exampleData.endDate ? new Date(exampleData.endDate) : null);

    setCustomBpjsJHT(null);
    setCustomBpjsJP(null);
    setPph26Rate(exampleData.pph26Rate || 20);

    setTimeout(() => {
      resultRef.current?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    }, 100);
  };

  // The slip's rows and its subtotals come from one array each, so a total can
  // never disagree with the lines printed above it.
  const grossIncome = yearlyCalculations
    ? yearlyCalculations.totalGross
    : calculations.totalGrossIncome;
  // The tax allowance is income to the employee, not a neutral line, which is
  // what makes gross-up circular. It is already inside the gross figure, so it
  // is split back out as a component rather than added to it.
  const taxAllowance = yearlyCalculations?.totalTaxAllowance || 0;

  const earningRows =
    taxAllowance > 0
      ? [
          {
            key: "baseEarnings",
            label: t("taxCalculator.results.baseEarnings"),
            amount: grossIncome - taxAllowance,
          },
          {
            key: "taxAllowance",
            label: t("taxCalculator.results.taxAllowance"),
            amount: taxAllowance,
          },
        ]
      : [
          {
            key: "gross",
            label: t("taxCalculator.results.totalGrossIncome"),
            amount: grossIncome,
          },
        ];

  const monthSuffix = ` (${workMonths} ${t("month")})`;
  // Without a per-year breakdown to read from, a row labelled "(N bulan)" has
  // to carry N months of the monthly figure - not one month of it.
  const periodAmount = (monthlyAmount) =>
    calculations.isYearlyCalculation
      ? (monthlyAmount || 0) * workMonths
      : monthlyAmount || 0;
  const deductionRows = [
    {
      key: "positionCost",
      label: t(
        employeeType.value === "retired"
          ? "taxCalculator.results.pensionCost"
          : "taxCalculator.results.positionCost",
      ),
      amount: yearlyCalculations
        ? yearlyCalculations.totalPositionCost
        : calculations.positionCost,
    },
  ];

  // A pensioner pays no JHT or JP, so the rows would only ever read Rp0.
  if (hasBPJS && employeeType.value === "permanent") {
    deductionRows.push(
      {
        key: "bpjsJHT",
        label: `${t("taxCalculator.results.bpjsJHT")}${
          calculations.isYearlyCalculation ? monthSuffix : ""
        }`,
        editTarget: "JHT",
        editLabel: t("taxCalculator.bpjs.editAmountJHT"),
        amount:
          calculations.isYearlyCalculation && yearlyCalculations
            ? yearlyCalculations.totalBpjsJHT
            : periodAmount(calculations.bpjsJHT),
      },
      {
        key: "bpjsJP",
        label: `${t("taxCalculator.results.bpjsJP")}${
          calculations.isYearlyCalculation ? monthSuffix : ""
        }`,
        editTarget: "JP",
        editLabel: t("taxCalculator.bpjs.editAmountJP"),
        amount:
          calculations.isYearlyCalculation && yearlyCalculations
            ? yearlyCalculations.totalBpjsJP
            : periodAmount(calculations.bpjsJP),
      },
    );
  }

  if (hasPensionContribution) {
    deductionRows.push({
      key: "pension",
      label: calculations.isYearlyCalculation
        ? `${t("taxCalculator.pension.contribution")}${monthSuffix}`
        : `${t("taxCalculator.pension.contribution")}/${t("month")}`,
      editTarget: "PENSION",
      editLabel: t("taxCalculator.pension.editAmount"),
      amount:
        calculations.isYearlyCalculation && yearlyCalculations
          ? yearlyCalculations.totalPension
          : periodAmount(customPensionAmount),
    });
  }

  if (hasZakat) {
    deductionRows.push({
      key: "zakat",
      label: calculations.isYearlyCalculation
        ? `${t("taxCalculator.zakat.label")}${monthSuffix}`
        : `${t("taxCalculator.zakat.label")}/${t("month")}`,
      editTarget: "ZAKAT",
      editLabel: t("taxCalculator.zakat.editAmount"),
      amount:
        calculations.isYearlyCalculation && yearlyCalculations
          ? yearlyCalculations.totalZakat
          : periodAmount(customZakatAmount),
    });
  }

  const sumRows = (rows) =>
    rows.reduce((sum, row) => sum + (row.amount || 0), 0);
  const totalEarnings = sumRows(earningRows);
  const totalDeductions = sumRows(deductionRows);

  // What the figure is for, in one caption beneath it - the same place the
  // other calculators put it - rather than split between the card subtitle and
  // the caption. The employee type comes first and is the only part allowed to
  // wrap, since it can run long. Only the facts that actually drive this
  // calculation belong here. The scheme is meaningless outside a permanent or retired employee,
  // and a period nobody chose would read as a claim about the figures.
  const resultContext = [t(employeeType.label)];
  if (isPermanentOrRetired) {
    resultContext.push(
      keepTogether(
        `${t(calculationPeriod.label)}${
          calculationPeriod.value === "yearly" && selectedYear
            ? ` ${selectedYear}`
            : ""
        }`,
      ),
      keepTogether(
        t(
          taxScheme.value === "GROSS_UP"
            ? "taxCalculator.taxScheme.grossUp"
            : "taxCalculator.taxScheme.gross",
        ),
      ),
    );
  } else if (employeeType.value === "non-permanent" && periodTypeOption) {
    resultContext.push(keepTogether(t(periodTypeOption.label)));
  }

  return (
    <div className="space-y-6">
      {renderModalBPJSEdit()}

      {/* Tax Calculation Examples Component */}
      <TaxCalculationExamples
        examples={PPH_2126_EXAMPLES}
        onSelectExample={handleSelectExample}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)] items-start gap-6">
        <div className="min-w-0 bg-white rounded-card border border-slate-200/60 shadow-sm divide-y divide-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6">
            <h3 className="text-base font-semibold text-slate-900">
              {t("taxCalculator.inputData")}
            </h3>
            <ResetButton onClick={handleReset} />
          </div>
          {/* The two sections are stacked, not side by side: they are never the
              same length, so the shorter column always leaves a hole. Within one
              grid, neighbouring fields sit on the same row. */}
          <section className="p-4 sm:p-6">
            <SectionHeading>
              {t("taxCalculator.sections.employee")}
            </SectionHeading>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
              {/* Freelancers, foreign employees and the one-off payment types
                  have nothing else to say about the person, so this select is
                  the whole section. Left at half width it would sit beside an
                  empty column. */}
              <div className={hasEmployeeDetailFields ? "" : "sm:col-span-2"}>
                <FormSelect
                  label="taxCalculator.employeeType.label"
                  value={employeeType}
                  onChange={(option) => setEmployeeType(option)}
                  options={EMPLOYEE_TYPE_OPTIONS}
                />
              </div>

              {employeeType.value === "permanent" && (
                <FormSelect
                  label="taxCalculator.taxScheme.label"
                  value={taxScheme}
                  onChange={(option) => setTaxScheme(option)}
                  options={TAX_METHOD_OPTIONS}
                />
              )}

              {hasMarriageStatusField && (
                <>
                  <FormSelect
                    label="taxCalculator.marriageStatus.label"
                    value={marriageStatus}
                    onChange={(option) => setMarriageStatus(option)}
                    options={MARRIAGE_STATUS_OPTIONS}
                  />

                  <FormSelect
                    label="taxCalculator.dependents"
                    value={dependents}
                    onChange={(option) => setDependents(option)}
                    placeholder={t("employees.placeholders.dependent")}
                    options={DEPENDENT_OPTIONS}
                    onClear={() => setDependents(null)}
                  />
                </>
              )}

              {employeeType.value === "permanent" && (
                <>
                  {/* The period and the input mode both say how the year is
                      expressed, so they share a row. With nothing to pair with,
                      the period takes the whole row rather than leaving a hole
                      beside it. */}
                  <FormRadioGroup
                    variant="segmented"
                    label="taxCalculator.calculationPeriod.label"
                    value={calculationPeriod}
                    onChange={setCalculationPeriod}
                    options={CALCULATION_PERIOD_OPTIONS}
                    className={isYearlyPeriod ? "" : "sm:col-span-2"}
                    gridClassName="grid-cols-2"
                    verticalSpace="py-2"
                  />

                  {isYearlyPeriod && (
                    <>
                      <FormRadioGroup
                        variant="segmented"
                        label="taxCalculator.inputMode.label"
                        value={inputMode}
                        onChange={setInputMode}
                        options={INPUT_MODE_OPTIONS}
                        gridClassName="grid-cols-2"
                        verticalSpace="py-2"
                      />

                      <FormRadioGroup
                        variant="segmented"
                        label="taxCalculator.calculationMethod.label"
                        value={calculationMethod}
                        onChange={setCalculationMethod}
                        options={CALCULATION_METHOD_OPTIONS}
                        className="sm:col-span-2"
                        gridClassName="grid-cols-1 sm:grid-cols-3"
                        verticalSpace="py-2"
                      />

                      {calculationMethod.value !== "full_year" && (
                        <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:col-span-2 sm:grid-cols-2">
                          <div>
                            <FormDate
                              label="employees.fields.startDate"
                              selected={startDate}
                              onChange={(date) => setStartDate(date)}
                              placeholderText={t(
                                "employees.placeholders.startDate",
                              )}
                              onClear={() => setStartDate(null)}
                              dateFormat="dd/MM/yyyy"
                            />
                            {!startDate && endDate && (
                              <p className="text-red-500 text-xs mt-1">
                                {t("taxCalculator.errors.startDateRequired")}
                              </p>
                            )}
                          </div>
                          <div>
                            <FormDate
                              label="employees.fields.endDate"
                              selected={endDate}
                              onChange={(date) => setEndDate(date)}
                              placeholderText={t(
                                "employees.placeholders.endDate",
                              )}
                              onClear={() => setEndDate(null)}
                              minDate={startDate}
                              dateFormat="dd/MM/yyyy"
                            />
                          </div>
                        </div>
                      )}

                      <p className="text-[13px] leading-relaxed text-slate-500 sm:col-span-2">
                        {t("taxCalculator.calculationMethod.annualizedHelp")}
                      </p>
                    </>
                  )}
                </>
              )}

              {employeeType.value === "retired" && (
                <FormRadioGroup
                  variant="segmented"
                  label="taxCalculator.calculationPeriod.label"
                  value={calculationPeriod}
                  onChange={setCalculationPeriod}
                  options={CALCULATION_PERIOD_OPTIONS}
                  gridClassName="grid-cols-2"
                  verticalSpace="py-2"
                />
              )}

              {employeeType.value === "non-permanent" && (
                <FormRadioGroup
                  variant="segmented"
                  label="taxCalculator.periodType.label"
                  value={periodTypeOption}
                  onChange={(option) => setIsMonthly(option.value)}
                  options={PERIOD_TYPE_OPTIONS}
                  gridClassName="grid-cols-2"
                  verticalSpace="py-2"
                />
              )}
            </div>
          </section>
          <section className="p-4 sm:p-6">
            <SectionHeading>
              {t("taxCalculator.sections.income")}
            </SectionHeading>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
              {employeeType.value === "retired" &&
                calculationPeriod.value === "yearly" && (
                  <>
                    <FormRadioGroup
                      variant="segmented"
                      label="taxCalculator.calculationMethod.label"
                      value={calculationMethod}
                      onChange={setCalculationMethod}
                      options={CALCULATION_METHOD_OPTIONS}
                      className="sm:col-span-2"
                      gridClassName="grid-cols-1 sm:grid-cols-3"
                      verticalSpace="py-2"
                    />

                    {calculationMethod.value !== "full_year" && (
                      <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:col-span-2 sm:grid-cols-2">
                        <div>
                          <FormDate
                            label="employees.fields.startDate"
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            placeholderText={t(
                              "employees.placeholders.startDate",
                            )}
                            onClear={() => setStartDate(null)}
                            dateFormat="dd/MM/yyyy"
                          />
                          {!startDate && endDate && (
                            <p className="text-red-500 text-xs mt-1">
                              {t("taxCalculator.errors.startDateRequired")}
                            </p>
                          )}
                        </div>
                        <div>
                          <FormDate
                            label="employees.fields.endDate"
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            placeholderText={t(
                              "employees.placeholders.endDate",
                            )}
                            onClear={() => setEndDate(null)}
                            minDate={startDate}
                            dateFormat="dd/MM/yyyy"
                          />
                        </div>
                      </div>
                    )}

                    <p className="text-[13px] leading-relaxed text-slate-500 sm:col-span-2">
                      {t("taxCalculator.calculationMethod.annualizedHelp")}
                    </p>

                    <FormRadioGroup
                      variant="segmented"
                      label="taxCalculator.inputMode.label"
                      value={inputMode}
                      onChange={setInputMode}
                      options={INPUT_MODE_OPTIONS}
                      className="sm:col-span-2"
                      gridClassName="grid-cols-2"
                      verticalSpace="py-2"
                    />
                  </>
                )}

              {employeeType.value === "non-permanent" && !isMonthly && (
                <>
                  <FormRadioGroup
                    variant="segmented"
                    label="taxCalculator.nonPermanent.wageType.label"
                    value={wageTypeOption}
                    onChange={setWageType}
                    options={WAGE_TYPE_OPTIONS}
                    gridClassName="grid-cols-2"
                    verticalSpace="py-2"
                  />

                  <FormInput
                    label={t("taxCalculator.nonPermanent.workingDays")}
                    name="workingDays"
                    type="number"
                    value={workingDays}
                    onChange={(e) => setWorkingDays(e.target.value)}
                    placeholder="0"
                    min="0"
                    max="31"
                  />

                  {wageType.value === "contract" && (
                    <FormInput
                      label={t("taxCalculator.nonPermanent.contractTotal")}
                      type="text"
                      value={contractTotal}
                      onChange={(e) => setContractTotal(e.target.value)}
                      placeholder="0"
                      numberFormat={{
                        thousandSeparator: ".",
                        decimalSeparator: ",",
                        allowNegative: false,
                        prefix: "Rp ",
                      }}
                    />
                  )}
                </>
              )}

              {(employeeType.value === "permanent" ||
                employeeType.value === "retired" ||
                employeeType.value === "freelance" ||
                PROGRESSIVE_TAX_TYPES.includes(employeeType.value) ||
                (employeeType.value === "non-permanent" && isMonthly)) &&
                hasIncomeInput && (
                  <div
                    className={
                      grossSalaryPairsWithNextField ? "" : "sm:col-span-2"
                    }
                  >
                    <FormLabel
                      action={
                        isPermanentOrRetired &&
                        calculationPeriod.value === "yearly" && (
                          <button
                            type="button"
                            onClick={() =>
                              setInputMode(
                                inputMode.value === "annual"
                                  ? INPUT_MODE_OPTIONS[0]
                                  : INPUT_MODE_OPTIONS[1],
                              )
                            }
                            className={QUIET_LINK_CLASS}
                          >
                            {t("taxCalculator.changeTo")}{" "}
                            {inputMode.value === "annual"
                              ? t("taxCalculator.switchToAnnualized")
                              : t("taxCalculator.switchToActual")}
                          </button>
                        )
                      }
                    >
                      {getFieldLabel("grossSalary")}
                    </FormLabel>
                    <div className="mt-1">
                      <FormInput
                        type="text"
                        aria-label={getFieldLabel("grossSalary")}
                        inputMode="decimal"
                        value={grossSalary}
                        onChange={(e) => setGrossSalary(e.target.value)}
                        placeholder="0"
                        numberFormat={{
                          thousandSeparator: ".",
                          decimalSeparator: ",",
                          allowNegative: false,
                          prefix: "Rp ",
                        }}
                      />
                    </div>
                  </div>
                )}

              {employeeType.value === "non-permanent" &&
                !isMonthly &&
                wageType.value === "daily" && (
                  <FormInput
                    label={getFieldLabel("grossSalary")}
                    type="text"
                    value={grossSalary}
                    onChange={(e) => setGrossSalary(e.target.value)}
                    placeholder="0"
                    numberFormat={{
                      thousandSeparator: ".",
                      decimalSeparator: ",",
                      allowNegative: false,
                      prefix: "Rp ",
                    }}
                  />
                )}

              {employeeType.value === "non-resident" && (
                <>
                  <div className="sm:col-span-2">
                    <FormInput
                      label={getFieldLabel("grossSalary")}
                      type="text"
                      value={grossSalary}
                      onChange={(e) => setGrossSalary(e.target.value)}
                      placeholder="0"
                      numberFormat={{
                        thousandSeparator: ".",
                        decimalSeparator: ",",
                        allowNegative: false,
                        prefix: "Rp ",
                      }}
                    />
                  </div>

                  <FormInput
                    label={getFieldLabel("allowances")}
                    type="text"
                    value={allowances}
                    onChange={(e) => setAllowances(e.target.value)}
                    placeholder="0"
                    numberFormat={{
                      thousandSeparator: ".",
                      decimalSeparator: ",",
                      allowNegative: false,
                      prefix: "Rp ",
                    }}
                  />

                  <FormInput
                    label={getFieldLabel("bonus")}
                    type="text"
                    value={bonus}
                    onChange={(e) => setBonus(e.target.value)}
                    placeholder="0"
                    numberFormat={{
                      thousandSeparator: ".",
                      decimalSeparator: ",",
                      allowNegative: false,
                      prefix: "Rp ",
                    }}
                  />

                  <FormInput
                    label={getFieldLabel("thr")}
                    type="text"
                    value={thr}
                    onChange={(e) => setThr(e.target.value)}
                    placeholder="0"
                    numberFormat={{
                      thousandSeparator: ".",
                      decimalSeparator: ",",
                      allowNegative: false,
                      prefix: "Rp ",
                    }}
                  />

                  <FormInput
                    label={getFieldLabel("overtime")}
                    type="text"
                    value={overtime}
                    onChange={(e) => setOvertime(e.target.value)}
                    placeholder="0"
                    numberFormat={{
                      thousandSeparator: ".",
                      decimalSeparator: ",",
                      allowNegative: false,
                      prefix: "Rp ",
                    }}
                  />

                  <FormInput
                    label={getFieldLabel("insurancePremium")}
                    type="text"
                    value={premium}
                    onChange={(e) => setPremium(e.target.value)}
                    placeholder="0"
                    numberFormat={{
                      thousandSeparator: ".",
                      decimalSeparator: ",",
                      allowNegative: false,
                      prefix: "Rp ",
                    }}
                  />
                </>
              )}

              {employeeType.value === "permanent" && (
                <>
                  {renderIncomeField("allowances", allowances, setAllowances, {
                    oneTimeToggle: false,
                  })}
                  {renderIncomeField(
                    "variableAllowances",
                    variableAllowances,
                    setVariableAllowances,
                    { oneTimeToggle: false },
                  )}
                  {renderIncomeField("bonus", bonus, setBonus)}
                  {renderIncomeField("thr", thr, setThr)}
                  {renderIncomeField("overtime", overtime, setOvertime)}
                  {renderIncomeField("insurancePremium", premium, setPremium, {
                    oneTimeToggle: false,
                  })}
                </>
              )}

              {hasOtherIncomeField &&
                renderIncomeField("otherIncome", otherIncome, setOtherIncome, {
                  oneTimeToggle: false,
                })}
            </div>
          </section>
          {isPermanentOrRetired && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50/70 rounded-b-card p-4 sm:p-6">
              {employeeType.value === "permanent" && (
                <div>
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-inner border border-slate-200 bg-white px-3 py-2 transition-colors hover:border-slate-300 has-[:checked]:border-blue-300 has-[:checked]:bg-blue-50">
                    <input
                      type="checkbox"
                      checked={hasBPJS}
                      onChange={(e) => setHasBPJS(e.target.checked)}
                      className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {t("taxCalculator.includeBPJS")}
                    </span>
                  </label>
                </div>
              )}

              <div>
                <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-inner border border-slate-200 bg-white px-3 py-2 transition-colors hover:border-slate-300 has-[:checked]:border-blue-300 has-[:checked]:bg-blue-50">
                  <input
                    type="checkbox"
                    checked={hasPensionContribution}
                    onChange={(e) =>
                      setHasPensionContribution(e.target.checked)
                    }
                    className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t("taxCalculator.pension.include")}
                  </span>
                </label>
              </div>

              <div>
                <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-inner border border-slate-200 bg-white px-3 py-2 transition-colors hover:border-slate-300 has-[:checked]:border-blue-300 has-[:checked]:bg-blue-50">
                  <input
                    type="checkbox"
                    checked={hasZakat}
                    onChange={(e) => setHasZakat(e.target.checked)}
                    className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t("taxCalculator.zakat.include")}
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        {hasIncomeInput && (
          <div ref={resultRef} className="min-w-0 scroll-mt-24 space-y-4">
            <CalculatorCard title={t("taxCalculator.results.title")}>
              <ResultHeadline
                label={
                  employeeType.value === "non-resident"
                    ? t("taxCalculator.results.pph26")
                    : t("taxCalculator.results.pph21")
                }
                value={formatMoney(
                  yearlyCalculations
                    ? yearlyCalculations.totalTax
                    : calculations.pph21,
                )}
                // A no-break space before each separator, so a narrow card
                // wraps after a dot and never opens a line with one.
                caption={resultContext.join("\u00A0· ")}
                action={
                  // The breakdown renders below the form, out of sight on
                  // most screens, so point to it from the result. Going
                  // somewhere on the page is navigation, so this is a plain
                  // fragment link: the browser also moves the keyboard's
                  // starting point to the table, which a scripted scroll
                  // leaves behind on the link.
                  monthlyBreakdown && calculations.isYearlyCalculation ? (
                    <a
                      href={`#${MONTHLY_BREAKDOWN_ID}`}
                      className="inline-flex min-h-[24px] items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 rounded"
                    >
                      {t("taxCalculator.monthlyBreakdown.viewLink")}
                      <ArrowDownIcon
                        className="h-4 w-4 shrink-0"
                        aria-hidden="true"
                      />
                    </a>
                  ) : null
                }
              />

              {/* Position cost only for permanent and retired employees */}
              {isPermanentOrRetired && (
                <>
                  <LedgerSection title={t("taxCalculator.results.earnings")}>
                    {earningRows.map((row) => (
                      <ResultRow
                        key={row.key}
                        label={row.label}
                        value={formatMoney(row.amount)}
                      />
                    ))}
                    {earningRows.length > 1 && (
                      <ResultRow
                        subtotal
                        label={t("taxCalculator.results.totalGrossIncome")}
                        value={formatMoney(totalEarnings)}
                      />
                    )}
                  </LedgerSection>

                  <LedgerSection title={t("taxCalculator.results.deductions")}>
                    {deductionRows.map((row) => (
                      <ResultRow
                        key={row.key}
                        sign="-"
                        label={row.label}
                        labelExtra={
                          row.editTarget ? (
                            <InlineEditButton
                              onClick={() =>
                                handleOpenBPJSModal(row.editTarget)
                              }
                              label={row.editLabel}
                            />
                          ) : null
                        }
                        value={formatMoney(row.amount)}
                      />
                    ))}
                    <ResultRow
                      subtotal
                      sign="-"
                      label={t("taxCalculator.results.totalDeductions")}
                      value={formatMoney(totalDeductions)}
                    />
                  </LedgerSection>

                  <ResultRow
                    bold
                    label={t("taxCalculator.results.netIncome")}
                    value={formatMoney(
                      yearlyCalculations
                        ? yearlyCalculations.netIncome
                        : calculations.netIncome,
                    )}
                  />
                </>
              )}

              {isPermanentOrRetired && (
                <>
                  {!calculations.isYearlyCalculation ? (
                    <LedgerSection title={t("taxCalculator.results.taxBasis")}>
                      <InfoBadge
                        label={t("taxCalculator.results.terCategory")}
                        value={calculations.terCategory}
                      />
                      <InfoBadge
                        label={t("taxCalculator.results.terRate")}
                        value={`${calculations.terRate.toFixed(2)}%`}
                      />
                    </LedgerSection>
                  ) : (
                    <DetailBox
                      title={
                        calculations.calculationMethod === "annualized"
                          ? `${t("taxCalculator.results.pkpCalculation")} (${t("taxCalculator.results.annualized")})`
                          : t("taxCalculator.results.pkpCalculation")
                      }
                    >
                      {calculations.calculationMethod === "annualized" &&
                      calculations.annualizedNetIncome !== null ? (
                        <>
                          <DetailRow
                            label={`${t("taxCalculator.results.netIncome")} (${t("taxCalculator.results.annualized")})`}
                            value={formatMoney(annualisedNetIncome)}
                          />
                          <DetailRow
                            sign="-"
                            label={`PTKP (${calculations.terCategory})`}
                            value={formatMoney(annualisedPtkpAmount)}
                          />
                          <DetailRow
                            label={`${t("taxCalculator.results.pkp")} (${t("taxCalculator.results.annualized")}, ${t("taxCalculator.results.pkpRounded")})`}
                            value={formatMoney(annualisedPkp)}
                            bold
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            {t("taxCalculator.results.annualizedHint")}
                          </p>
                        </>
                      ) : (
                        <>
                          <DetailRow
                            label={t("taxCalculator.results.netIncome")}
                            value={formatMoney(
                              yearlyCalculations
                                ? yearlyCalculations.netIncome
                                : calculations.netIncome,
                            )}
                          />
                          <DetailRow
                            sign="-"
                            label={`PTKP (${calculations.terCategory})`}
                            value={formatMoney(
                              yearlyCalculations
                                ? yearlyCalculations.ptkpAmount
                                : calculations.ptkpAmount,
                            )}
                          />
                          <DetailRow
                            label={`${t("taxCalculator.results.pkp")} (${t("taxCalculator.results.pkpRounded")})`}
                            value={formatMoney(
                              yearlyCalculations
                                ? yearlyCalculations.pkp
                                : calculations.pkp,
                            )}
                            bold
                          />
                        </>
                      )}

                      {(yearlyCalculations
                        ? yearlyCalculations.pkp
                        : calculations.pkp) > 0 && (
                        <>
                          <div className="pt-3 border-t border-slate-100">
                            <SectionHeading>
                              {t(
                                "taxCalculator.results.progressiveTaxBreakdown",
                              )}
                            </SectionHeading>
                          </div>
                          {TAX_BRACKETS.map((bracket, index) => {
                            const pkpValue = yearlyCalculations
                              ? yearlyCalculations.pkp
                              : calculations.pkp;
                            const prevMax =
                              index > 0 ? TAX_BRACKETS[index - 1].max : 0;
                            const taxableAmount = Math.min(
                              Math.max(0, pkpValue - prevMax),
                              bracket.max - prevMax,
                            );

                            if (taxableAmount <= 0) return null;

                            const tax = taxableAmount * bracket.rate;

                            return (
                              <DetailRow
                                key={index}
                                label={`${formatMoney(taxableAmount)} × ${(bracket.rate * 100).toFixed(0)}%`}
                                value={formatMoney(tax)}
                              />
                            );
                          })}
                          {yearlyCalculations?.annualisedTaxableIncome > 0 && (
                            <DetailRow
                              label={t("taxCalculator.results.proratedTax", {
                                months: yearlyCalculations.monthCount,
                              })}
                              value={formatMoney(yearlyCalculations.totalTax)}
                              bold
                            />
                          )}
                        </>
                      )}
                    </DetailBox>
                  )}
                </>
              )}

              {employeeType.value === "non-permanent" && (
                <DetailBox
                  title={
                    isMonthly
                      ? t("taxCalculator.calculations.terMonthly")
                      : t("taxCalculator.calculations.terDaily")
                  }
                >
                  {isMonthly ? (
                    <>
                      <DetailRow
                        label={t("taxCalculator.nonPermanent.monthlyGross")}
                        value={formatMoney(calculations.totalGrossIncome)}
                      />
                      <DetailRow
                        label={t("taxCalculator.results.terCategory")}
                        value={calculations.terCategory}
                      />
                      <DetailRow
                        label={`${t("taxCalculator.results.terRate")} (${t("taxCalculator.periodType.monthly")})`}
                        value={`${calculations.terRate.toFixed(2)}%`}
                      />
                    </>
                  ) : calculations.dailyCalculationResult?.method ===
                    "TER_DAILY" ? (
                    <>
                      <DetailRow
                        label={t("taxCalculator.nonPermanent.wageType.daily")}
                        value={formatMoney(parsedInputs.gross)}
                      />
                      <DetailRow
                        label={t("taxCalculator.nonPermanent.workingDays")}
                        value={`${parseInt(workingDays) || 0} ${t("taxCalculator.nonPermanent.days")}`}
                      />
                      <DetailRow
                        label={t(
                          "taxCalculator.nonPermanent.totalMonthlyGross",
                        )}
                        value={formatMoney(
                          calculations.dailyCalculationResult?.monthlyGross ||
                            0,
                        )}
                      />
                      <DetailRow
                        label={`${t("taxCalculator.results.terRate")} (${t("taxCalculator.periodType.daily")})`}
                        value={`${((calculations.dailyCalculationResult?.terRate || 0) * 100).toFixed(2)}%`}
                      />
                      <DetailRow
                        label={t("taxCalculator.nonPermanent.dailyTax")}
                        value={formatMoney(
                          calculations.dailyCalculationResult?.dailyTax || 0,
                        )}
                        bold
                      />
                    </>
                  ) : (
                    <>
                      <DetailRow
                        label={t("taxCalculator.nonPermanent.wageType.daily")}
                        value={formatMoney(parsedInputs.gross)}
                      />
                      <DetailRow
                        label={t("taxCalculator.nonPermanent.workingDays")}
                        value={`${parseInt(workingDays) || 0} ${t("taxCalculator.nonPermanent.days")}`}
                      />
                      <DetailRow
                        label={t(
                          "taxCalculator.nonPermanent.totalMonthlyGross",
                        )}
                        value={formatMoney(
                          calculations.dailyCalculationResult?.monthlyGross ||
                            0,
                        )}
                      />
                      <DetailRow
                        label={`${t("taxCalculator.results.dpp")} (50% × ${t("taxCalculator.nonPermanent.wageType.daily")})`}
                        value={formatMoney(
                          calculations.dailyCalculationResult?.dpp || 0,
                        )}
                      />
                      <div className="pt-3 border-t border-slate-100">
                        <SectionHeading>
                          {t("taxCalculator.results.progressiveTaxBreakdown")}
                        </SectionHeading>
                      </div>
                      {TAX_BRACKETS.map((bracket, index) => {
                        const dpp =
                          calculations.dailyCalculationResult?.dpp || 0;
                        const prevMax =
                          index > 0 ? TAX_BRACKETS[index - 1].max : 0;
                        const taxableAmount = Math.min(
                          Math.max(0, dpp - prevMax),
                          bracket.max - prevMax,
                        );
                        if (taxableAmount <= 0) return null;
                        return (
                          <DetailRow
                            key={index}
                            label={`${formatMoney(taxableAmount)} × ${(bracket.rate * 100).toFixed(0)}%`}
                            value={formatMoney(taxableAmount * bracket.rate)}
                          />
                        );
                      })}
                      <DetailRow
                        label={t("taxCalculator.nonPermanent.dailyTax")}
                        value={formatMoney(
                          calculations.dailyCalculationResult?.dailyTax || 0,
                        )}
                        bold
                      />
                    </>
                  )}
                </DetailBox>
              )}

              {employeeType.value === "freelance" && (
                <DetailBox title={t("taxCalculator.calculations.freelance")}>
                  <DetailRow
                    label={t("taxCalculator.monthlyBreakdown.grossIncome")}
                    value={formatMoney(calculations.totalGrossIncome)}
                  />
                  <DetailRow
                    label={`${t("taxCalculator.results.dpp")} (50% × ${t("taxCalculator.monthlyBreakdown.grossIncome")})`}
                    value={formatMoney(calculations.pkp)}
                  />
                  <div className="pt-3 border-t border-slate-100">
                    <SectionHeading>
                      {t("taxCalculator.results.progressiveTaxBreakdown")}
                    </SectionHeading>
                  </div>
                  {TAX_BRACKETS.map((bracket, index) => {
                    const dpp = calculations.pkp;
                    const prevMax = index > 0 ? TAX_BRACKETS[index - 1].max : 0;
                    const taxableAmount = Math.min(
                      Math.max(0, dpp - prevMax),
                      bracket.max - prevMax,
                    );
                    if (taxableAmount <= 0) return null;
                    const tax = taxableAmount * bracket.rate;
                    return (
                      <DetailRow
                        key={index}
                        label={`${formatMoney(taxableAmount)} × ${(bracket.rate * 100).toFixed(0)}%`}
                        value={formatMoney(tax)}
                      />
                    );
                  })}
                </DetailBox>
              )}

              {PROGRESSIVE_TAX_TYPES.includes(employeeType.value) && (
                <DetailBox
                  title={t(
                    employeeType.value === "activity-participant"
                      ? "taxCalculator.calculations.activityParticipant"
                      : employeeType.value === "pension-withdrawal"
                        ? "taxCalculator.calculations.pensionWithdrawal"
                        : "taxCalculator.calculations.formerEmployee",
                  )}
                >
                  <DetailRow
                    label={t("taxCalculator.monthlyBreakdown.grossIncome")}
                    value={formatMoney(calculations.totalGrossIncome)}
                  />
                  <div className="pt-3 border-t border-slate-100">
                    <SectionHeading>
                      {t("taxCalculator.results.progressiveTaxBreakdown")}
                    </SectionHeading>
                  </div>
                  {TAX_BRACKETS.map((bracket, index) => {
                    const grossIncome = calculations.totalGrossIncome;
                    const prevMax = index > 0 ? TAX_BRACKETS[index - 1].max : 0;
                    const taxableAmount = Math.min(
                      Math.max(0, grossIncome - prevMax),
                      bracket.max - prevMax,
                    );
                    if (taxableAmount <= 0) return null;
                    const tax = taxableAmount * bracket.rate;
                    return (
                      <DetailRow
                        key={index}
                        label={`${formatMoney(taxableAmount)} × ${(bracket.rate * 100).toFixed(0)}%`}
                        value={formatMoney(tax)}
                      />
                    );
                  })}
                </DetailBox>
              )}

              {employeeType.value === "non-resident" && (
                <DetailBox title={t("taxCalculator.calculations.pph26")}>
                  <DetailRow
                    label={t("taxCalculator.monthlyBreakdown.grossIncome")}
                    value={formatMoney(calculations.totalGrossIncome)}
                  />
                  <DetailRow
                    label={
                      <span className="flex items-center gap-1.5">
                        {t("taxCalculator.pph26.rate")}
                        <InlineEditButton
                          onClick={() => handleOpenBPJSModal("PPH26_RATE")}
                          label={t("taxCalculator.pph26.editRate")}
                        />
                      </span>
                    }
                    value={`${pph26Rate}%`}
                  />
                  <FormulaRow
                    label={t("taxCalculator.calculations.detail")}
                    value={`${pph26Rate}% × ${formatMoney(calculations.totalGrossIncome)}`}
                  />
                </DetailBox>
              )}

              <ResultRow
                total
                label={
                  employeeType.value === "non-resident"
                    ? t("taxCalculator.results.pph26")
                    : t("taxCalculator.results.pph21")
                }
                value={formatMoney(
                  yearlyCalculations
                    ? yearlyCalculations.totalTax
                    : calculations.pph21,
                )}
              />
            </CalculatorCard>
          </div>
        )}
      </div>

      {/* Monthly Breakdown Table */}
      {renderMonthlyBreakdownTable()}

      {/* Knowledge Base / Educational SEO Footer */}
      <KnowledgeBaseSection keys={PPH2126_KB_KEYS} />
    </div>
  );
};

export default CalculatorPph2126;
